<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  cortexAgentsAPI,
  cortexAuditEventsAPI,
  cortexKnowledgeBasesAPI,
  cortexPlaygroundRunsAPI,
  cortexToolExecutionsAPI,
  cortexToolsAPI,
} from 'dashboard/api/cortex';
import { externalCredentialsAPI } from 'dashboard/api/externalCredentials';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import PortalsAPI from 'dashboard/api/helpCenter/portals';
import categoriesAPI from 'dashboard/api/helpCenter/categories';
import macrosAPI from 'dashboard/api/macros';
import automationsAPI from 'dashboard/api/automation';
import attributesAPI from 'dashboard/api/attributes';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ModuleSettingsLayout from 'dashboard/components-next/layout/ModuleSettingsLayout.vue';
import SettingsListGroup from 'dashboard/components-next/layout/SettingsListGroup.vue';
import ResourceHelpActions from 'dashboard/components/widgets/ResourceHelpActions.vue';
import { useProductTour } from 'dashboard/composables/useProductTour';
import { CONTEXTUAL_HELP_KEYS } from 'dashboard/constants/contextualHelp';
import { getUnixEndOfDay, getUnixStartOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import CortexAgentWizard from '../components/CortexAgentWizard.vue';
import CortexKnowledgeBaseDialog from '../components/CortexKnowledgeBaseDialog.vue';
import CortexSettings from '../components/CortexSettings.vue';
import CortexUsageCosts from '../components/CortexUsageCosts.vue';
import CortexProcessingWorkspace from '../components/CortexProcessingWorkspace.vue';
import CortexSectionGuide from '../components/CortexSectionGuide.vue';
import CortexWebSources from '../components/CortexWebSources.vue';
import CortexPlaygroundExecutionFeedback from '../components/CortexPlaygroundExecutionFeedback.vue';
import CortexResponseViewerDialog from '../components/CortexResponseViewerDialog.vue';
import CortexToolDialog from '../components/CortexToolDialog.vue';
import CortexToolTestDialog from '../components/CortexToolTestDialog.vue';
import CortexToolTestResultDialog from '../components/CortexToolTestResultDialog.vue';
import { getCortexNavigationItems } from '../cortexNavigation';
import { hasReadyModelConfiguration } from '../modelReadiness';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { checkPermissions } = usePolicy();
const cortexHelpSystemKey = CONTEXTUAL_HELP_KEYS.CORTEX;
const cortexArticleKeyByTab = {
  overview: 'manual.cortex.overview',
  agents: 'manual.cortex.agents',
  knowledge: 'manual.cortex.knowledge',
  settings: 'manual.cortex.models',
  tools: 'manual.cortex.tools',
  playground: 'manual.cortex.playground',
  processing: 'manual.cortex.processing',
  costs: 'manual.cortex.costs',
  audit: 'manual.cortex.audit',
};
const { startIfPending: startCortexTourIfPending } =
  useProductTour(cortexHelpSystemKey);

const CORTEX_PERMISSIONS = ['administrator', 'can_manage_cortex'];
const CORTEX_AUDIT_PERMISSIONS = ['administrator', 'can_view_cortex_audit'];
const sectionTargetByQuery = {
  agent_knowledge: 'cortex-agent-knowledge',
  agent_status: 'cortex-agent-status-section',
  documents: 'cortex-knowledge-documents',
  help_center: 'cortex-knowledge-help-center',
  sources: 'cortex-knowledge-sources',
  structured_import: 'cortex-knowledge-structured-import',
  tool_create: 'cortex-tool-create',
  tool_executions: 'cortex-tool-executions',
  tool_native: 'cortex-tool-native',
  tool_health: 'cortex-tool-health',
};

const TOOL_HEALTH_POLL_INTERVAL = 15000;
const isLoading = ref(false);
const isDeletingAgent = ref(false);
const isSavingKnowledgeBase = ref(false);
const isUpdatingKnowledgeBase = ref(false);
const isDeletingKnowledgeBase = ref(false);
const isSavingDocument = ref(false);
const deletingDocumentId = ref(null);
const isPreviewingDocument = ref(false);
const isLoadingDocumentContent = ref(false);
const isSyncingHelpCenter = ref(false);
const isPreviewingStructuredImport = ref(false);
const isImportingStructured = ref(false);
const isSavingTool = ref(false);
const isTestingTool = ref(false);
const isDeletingToolId = ref(null);
const isSavingNativeTool = ref(false);
const isUpdatingToolExecution = ref(false);
const isCheckingToolHealth = ref(false);
const isToolHealthMonitoringEnabled = ref(true);
const toolMonitoringDateRange = ref([subDays(new Date(), 6), new Date()]);
const toolMonitoringRangeType = ref(DATE_RANGE_TYPES.LAST_7_DAYS);

const formatMonitoringNumber = value => Number(value || 0).toLocaleString();
const formatMonitoringCurrency = value =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  }).format(Number(value || 0));
const isRunningPlayground = ref(false);
const selectedAgentId = ref(null);
const selectedPlaygroundAgentId = ref(null);
const selectedKnowledgeBaseId = ref(null);
const activeKnowledgeSection = ref('documents');
const activePlaygroundSection = ref('chat');
const activeToolSection = ref('create');
const nativeToolPagination = ref({
  page: 1,
  per_page: 10,
});
const agents = ref([]);
const knowledgeBases = ref([]);
const portals = ref([]);
const helpCenterCategories = ref([]);
const knowledgeSources = ref([]);
const documents = ref([]);
const documentPagination = ref({ page: 1, per_page: 10, total: 0 });
const documentSourceFilter = ref(null);
const structuredImportPreview = ref(null);
const tools = ref([]);
const externalCredentials = ref([]);
const contactCustomAttributes = ref([]);
const toolHealthchecks = ref([]);
const toolMonitoring = ref(null);
const toolExecutions = ref([]);
const macros = ref([]);
const automationRules = ref([]);
const auditEvents = ref([]);
const playgroundRuns = ref([]);
const playgroundResultFilters = reactive({
  query: '',
  reviewState: 'all',
});
const playgroundResultPagination = ref({
  page: 1,
  per_page: 10,
});
const cortexSettings = ref(null);

const showAgentWizard = ref(false);
const editingAgent = ref(null);
const showKnowledgeBaseDialog = ref(false);
const editingKnowledgeBase = ref(null);
const showToolDialog = ref(false);
const editingTool = ref(null);
const toolContextMenu = ref(null);
const toolContextMenuPosition = reactive({ x: 0, y: 0 });
const toolTestPayload = ref(null);
const toolTestResult = ref(null);
const destructiveActionDialog = ref(null);
const documentViewerDialog = ref(null);
const viewedDocument = ref(null);
const viewedDocumentChunks = ref([]);
const documentContentPagination = ref({ page: 1, per_page: 10, total: 0 });
const destructiveActionDescription = ref('');
const pendingDestructiveAction = ref(null);
const isRunningDestructiveAction = ref(false);

const formattedDocumentChunk = content => {
  const formatter = new MessageFormatter(content);
  formatter.disableImageRendering();
  return formatter.formattedMessage;
};

const openEditWizard = agent => {
  editingAgent.value = agent;
  showAgentWizard.value = true;
};

const openCreateKnowledgeBaseDialog = () => {
  editingKnowledgeBase.value = null;
  showKnowledgeBaseDialog.value = true;
};

const helpCenterForm = reactive({
  portal_ids: [],
  locales: [],
});
const structuredImportForm = reactive({
  source_name: '',
  rows: '',
  schema: '',
});
const documentForm = reactive({
  name: '',
  document_kind: 'standard',
});
const selectedDocumentFile = ref(null);
const documentFileInputRef = ref(null);
const documentPreview = ref(null);
const playgroundForm = reactive({
  message: '',
});
const playgroundTraining = reactive({
  runId: null,
  answer: '',
  knowledgeBaseId: '',
});
const isSavingPlaygroundTraining = ref(false);
const isRetestingPlaygroundRunId = ref(null);
const nativeToolForm = reactive({
  macro_id: '',
  automation_rule_id: '',
});

const tabs = computed(() =>
  getCortexNavigationItems({
    canManage: checkPermissions(CORTEX_PERMISSIONS),
    canViewAudit: checkPermissions(CORTEX_AUDIT_PERMISSIONS),
    translate: t,
  })
);

const agentStatusOptions = computed(() => [
  {
    value: 'draft',
    label: t('CORTEX.AGENTS.STATUS.draft'),
  },
  {
    value: 'testing',
    label: t('CORTEX.AGENTS.STATUS.testing'),
  },
  {
    value: 'active',
    label: t('CORTEX.AGENTS.STATUS.active'),
  },
  {
    value: 'paused',
    label: t('CORTEX.AGENTS.STATUS.paused'),
  },
  {
    value: 'archived',
    label: t('CORTEX.AGENTS.STATUS.archived'),
  },
]);

const activeTab = computed(() => {
  const navigationPath = route.params.navigationPath;
  return tabs.value.some(tab => tab.key === navigationPath)
    ? navigationPath
    : tabs.value[0]?.key || 'agents';
});
const cortexHelpArticleKey = computed(
  () => cortexArticleKeyByTab[activeTab.value]
);

const primaryTabs = computed(() => tabs.value.slice(0, 5));
const secondaryTabs = computed(() => tabs.value.slice(5));
const primaryActiveTabIndex = computed(() =>
  primaryTabs.value.findIndex(tab => tab.key === activeTab.value)
);
const secondaryActiveTabIndex = computed(() =>
  secondaryTabs.value.findIndex(tab => tab.key === activeTab.value)
);
const canViewCortexAudit = computed(() =>
  checkPermissions(CORTEX_AUDIT_PERMISSIONS)
);
const canManageCortex = computed(() => checkPermissions(CORTEX_PERMISSIONS));
const playgroundTabs = computed(() => [
  {
    key: 'chat',
    icon: 'i-lucide-sparkles',
    label: t('CORTEX.PLAYGROUND.TABS.CHAT'),
    description: t('CORTEX.PLAYGROUND.GUIDE.CHAT.DESCRIPTION'),
  },
  {
    key: 'results',
    icon: 'i-lucide-list-checks',
    label: t('CORTEX.PLAYGROUND.TABS.RESULTS'),
    count: playgroundRuns.value.length,
    description: t('CORTEX.PLAYGROUND.GUIDE.RESULTS.DESCRIPTION'),
  },
  {
    key: 'training',
    icon: 'i-lucide-graduation-cap',
    label: t('CORTEX.PLAYGROUND.TABS.TRAINING'),
    description: t('CORTEX.PLAYGROUND.GUIDE.TRAINING.DESCRIPTION'),
  },
]);
const activePlaygroundTabIndex = computed(() =>
  playgroundTabs.value.findIndex(
    tab => tab.key === activePlaygroundSection.value
  )
);

const overviewStats = computed(() => [
  {
    key: 'agents',
    label: t('CORTEX.OVERVIEW.STATS.AGENTS'),
    value: agents.value.length,
    icon: 'i-lucide-bot',
  },
  {
    key: 'knowledge',
    label: t('CORTEX.OVERVIEW.STATS.KNOWLEDGE_BASES'),
    value: knowledgeBases.value.length,
    icon: 'i-lucide-book-open',
  },
  {
    key: 'tools',
    label: t('CORTEX.OVERVIEW.STATS.TOOLS'),
    value: tools.value.length,
    icon: 'i-lucide-wrench',
  },
]);

const defaultConversionToolIds = [
  'cortex_conversion_document_markdown',
  'cortex_conversion_image_ocr',
  'cortex_conversion_audio_video_transcription',
];

const hasReadyModelSettings = computed(() =>
  hasReadyModelConfiguration(cortexSettings.value)
);

const hasReadyConversionSettings = computed(() => {
  const conversion = cortexSettings.value?.conversion || {};
  return !!(
    conversion.timeout_seconds &&
    conversion.max_file_size_mb &&
    conversion.ocr_languages
  );
});

const hasReadyDefaultConversionTools = computed(() => {
  const activeSystemToolIds = new Set(
    tools.value
      .filter(tool => tool.status === 'active')
      .map(tool => tool.config?.system_tool_id)
      .filter(Boolean)
  );

  return defaultConversionToolIds.every(toolId =>
    activeSystemToolIds.has(toolId)
  );
});

const overviewChecklistStatusLabel = status =>
  ({
    ready: t('CORTEX.OVERVIEW.CHECKLIST_STATUS.READY'),
    needs_action: t('CORTEX.OVERVIEW.CHECKLIST_STATUS.NEEDS_ACTION'),
    review: t('CORTEX.OVERVIEW.CHECKLIST_STATUS.REVIEW'),
  })[status];

const overviewChecklistStatusColor = status =>
  ({
    ready: 'teal',
    needs_action: 'amber',
    review: 'slate',
  })[status] || 'slate';

const overviewCards = computed(() =>
  [
    {
      key: 'agents',
      icon: 'i-lucide-bot',
      title: t('CORTEX.OVERVIEW.CARDS.AGENTS.TITLE'),
      description: t('CORTEX.OVERVIEW.CARDS.AGENTS.DESCRIPTION'),
      action: t('CORTEX.OVERVIEW.CARDS.AGENTS.ACTION'),
      route: 'agents',
      section: 'agent_status',
    },
    {
      key: 'knowledge',
      icon: 'i-lucide-book-open',
      title: t('CORTEX.OVERVIEW.CARDS.KNOWLEDGE.TITLE'),
      description: t('CORTEX.OVERVIEW.CARDS.KNOWLEDGE.DESCRIPTION'),
      action: t('CORTEX.OVERVIEW.CARDS.KNOWLEDGE.ACTION'),
      route: 'knowledge',
      section: 'documents',
    },
    {
      key: 'settings',
      icon: 'i-lucide-settings-2',
      title: t('CORTEX.OVERVIEW.CARDS.MODELS.TITLE'),
      description: t('CORTEX.OVERVIEW.CARDS.MODELS.DESCRIPTION'),
      action: t('CORTEX.OVERVIEW.CARDS.MODELS.ACTION'),
      route: 'settings',
      section: 'models',
    },
    {
      key: 'tools',
      icon: 'i-lucide-wrench',
      title: t('CORTEX.OVERVIEW.CARDS.TOOLS.TITLE'),
      description: t('CORTEX.OVERVIEW.CARDS.TOOLS.DESCRIPTION'),
      action: t('CORTEX.OVERVIEW.CARDS.TOOLS.ACTION'),
      route: 'tools',
      section: 'tool_create',
    },
    {
      key: 'costs',
      icon: 'i-lucide-chart-column',
      title: t('CORTEX.OVERVIEW.CARDS.COSTS.TITLE'),
      description: t('CORTEX.OVERVIEW.CARDS.COSTS.DESCRIPTION'),
      action: t('CORTEX.OVERVIEW.CARDS.COSTS.ACTION'),
      route: 'costs',
    },
    canViewCortexAudit.value
      ? {
          key: 'audit',
          icon: 'i-lucide-scroll-text',
          title: t('CORTEX.OVERVIEW.CARDS.AUDIT.TITLE'),
          description: t('CORTEX.OVERVIEW.CARDS.AUDIT.DESCRIPTION'),
          action: t('CORTEX.OVERVIEW.CARDS.AUDIT.ACTION'),
          route: 'audit',
        }
      : null,
  ].filter(Boolean)
);

const overviewChecklist = computed(() => [
  {
    key: 'model',
    label: t('CORTEX.OVERVIEW.CHECKLIST.MODEL'),
    description: t('CORTEX.OVERVIEW.CHECKLIST_HELP.MODEL'),
    status: hasReadyModelSettings.value ? 'ready' : 'needs_action',
    route: 'settings',
    section: 'models',
  },
  {
    key: 'conversion',
    label: t('CORTEX.OVERVIEW.CHECKLIST.CONVERSION'),
    description: t('CORTEX.OVERVIEW.CHECKLIST_HELP.CONVERSION'),
    status:
      hasReadyConversionSettings.value && hasReadyDefaultConversionTools.value
        ? 'ready'
        : 'needs_action',
    route: 'settings',
    section: 'conversions',
  },
  {
    key: 'knowledge',
    label: t('CORTEX.OVERVIEW.CHECKLIST.KNOWLEDGE'),
    description: t('CORTEX.OVERVIEW.CHECKLIST_HELP.KNOWLEDGE'),
    status: knowledgeBases.value.length ? 'ready' : 'needs_action',
    route: 'knowledge',
    section: 'documents',
  },
  {
    key: 'agent',
    label: t('CORTEX.OVERVIEW.CHECKLIST.AGENT'),
    description: t('CORTEX.OVERVIEW.CHECKLIST_HELP.AGENT'),
    status: agents.value.some(agent => agent.status === 'active')
      ? 'ready'
      : 'needs_action',
    route: 'agents',
    section: 'agent_status',
  },
  {
    key: 'inbox',
    label: t('CORTEX.OVERVIEW.CHECKLIST.INBOX'),
    description: t('CORTEX.OVERVIEW.CHECKLIST_HELP.INBOX'),
    status: agents.value.some(agent => Number(agent.active_inboxes_count) > 0)
      ? 'ready'
      : 'needs_action',
    route: 'agents',
    section: 'inboxes',
  },
]);

const goToCortexSection = (navigationPath, section) => {
  router.push(
    accountScopedRoute(
      'cortex_index',
      { navigationPath },
      section ? { section } : undefined
    )
  );
};

const selectedAgent = computed(() =>
  agents.value.find(agent => agent.id === selectedAgentId.value)
);

const agentSections = computed(() => [
  {
    key: 'agents',
    icon: 'i-lucide-bot',
    label: t('CORTEX.AGENTS.LIST_TITLE'),
    description: t('CORTEX.AGENTS.LIST_DESCRIPTION'),
  },
]);

const knowledgeSectionLinks = computed(() => [
  {
    key: 'documents',
    icon: 'i-lucide-files',
    label: t('CORTEX.KNOWLEDGE.DOCUMENTS.TITLE'),
    description: t('CORTEX.KNOWLEDGE.DOCUMENTS.DESCRIPTION'),
    target: 'cortex-knowledge-documents',
  },
  {
    key: 'sources',
    icon: 'i-lucide-globe-2',
    label: t('CORTEX.KNOWLEDGE.SOURCES.TITLE'),
    description: t('CORTEX.KNOWLEDGE.SOURCES.DESCRIPTION'),
    target: 'cortex-knowledge-sources',
  },
  {
    key: 'help_center',
    icon: 'i-lucide-book-open-check',
    label: t('CORTEX.KNOWLEDGE.HELP_CENTER.TITLE'),
    description: t('CORTEX.KNOWLEDGE.HELP_CENTER.DESCRIPTION'),
    target: 'cortex-knowledge-help-center',
  },
  {
    key: 'structured_import',
    icon: 'i-lucide-table-properties',
    label: t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.TITLE'),
    description: t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.DESCRIPTION'),
    target: 'cortex-knowledge-structured-import',
  },
]);

const auditSections = computed(() => [
  {
    key: 'events',
    icon: 'i-lucide-scroll-text',
    label: t('CORTEX.AUDIT.EVENTS_TITLE'),
    description: t('CORTEX.AUDIT.EVENTS_DESCRIPTION'),
  },
]);

const toolTabs = computed(() => [
  {
    key: 'create',
    icon: 'i-lucide-plus',
    label: t('CORTEX.TOOLS.FORM.SUBMIT'),
    description: t('CORTEX.TOOLS.FORM.CREATE_DESCRIPTION'),
  },
  {
    key: 'health',
    icon: 'i-lucide-activity',
    label: t('CORTEX.TOOLS.HEALTH.TITLE'),
    description: t('CORTEX.TOOLS.HEALTH.DESCRIPTION'),
  },
  {
    key: 'native',
    icon: 'i-lucide-wand-sparkles',
    label: t('CORTEX.TOOLS.NATIVE.TITLE'),
    description: t('CORTEX.TOOLS.NATIVE.DESCRIPTION'),
  },
  {
    key: 'executions',
    icon: 'i-lucide-list-checks',
    label: t('CORTEX.TOOLS.EXECUTIONS.TITLE'),
    description: t('CORTEX.TOOLS.EXECUTIONS.DESCRIPTION'),
  },
]);
const activeToolTabIndex = computed(() =>
  toolTabs.value.findIndex(tab => tab.key === activeToolSection.value)
);
const activeToolSectionDetails = computed(() =>
  toolTabs.value.find(tab => tab.key === activeToolSection.value)
);
const nativeTools = computed(() =>
  tools.value.filter(tool =>
    ['native', 'macro', 'automation'].includes(tool.tool_type)
  )
);
const monitoredTools = computed(() =>
  tools.value.filter(
    tool => !['native', 'macro', 'automation'].includes(tool.tool_type)
  )
);
const externalHttpTools = computed(() =>
  tools.value.filter(tool => tool.tool_type === 'http_api')
);
const paginatedNativeTools = computed(() => {
  const { page, per_page: perPage } = nativeToolPagination.value;
  return nativeTools.value.slice((page - 1) * perPage, page * perPage);
});

const toolTypeLabels = computed(() => ({
  native: t('CORTEX.TOOLS.TYPE.native'),
  macro: t('CORTEX.TOOLS.TYPE.macro'),
  automation: t('CORTEX.TOOLS.TYPE.automation'),
  http_api: t('CORTEX.TOOLS.TYPE.http_api'),
  crawler: t('CORTEX.TOOLS.TYPE.crawler'),
  storage: t('CORTEX.TOOLS.TYPE.storage'),
  ocr: t('CORTEX.TOOLS.TYPE.ocr'),
  transcription: t('CORTEX.TOOLS.TYPE.transcription'),
  knowledge: t('CORTEX.TOOLS.TYPE.knowledge'),
  diagnostic: t('CORTEX.TOOLS.TYPE.diagnostic'),
}));
const toolStatusLabels = computed(() => ({
  draft: t('CORTEX.TOOLS.STATUS.draft'),
  active: t('CORTEX.TOOLS.STATUS.active'),
  disabled: t('CORTEX.TOOLS.STATUS.disabled'),
  archived: t('CORTEX.TOOLS.STATUS.archived'),
}));
const toolRiskLabels = computed(() => ({
  low: t('CORTEX.TOOLS.RISK.low'),
  medium: t('CORTEX.TOOLS.RISK.medium'),
  high: t('CORTEX.TOOLS.RISK.high'),
  critical: t('CORTEX.TOOLS.RISK.critical'),
}));
const toolTypeLabel = toolType => toolTypeLabels.value[toolType] || toolType;
const toolStatusLabel = status => toolStatusLabels.value[status] || status;
const toolRiskLabel = riskLevel => toolRiskLabels.value[riskLevel] || riskLevel;
const toolMethodLabel = method =>
  t(`CORTEX.TOOLS.FORM.METHODS.${method || 'get'}`);
const isSystemTool = tool => tool.config?.system_tool === true;
const isConversionTool = tool => tool.config?.conversion_tool === true;
const isExtractionTool = tool => tool.config?.system_dependency === 'firecrawl';
const toolStatusColor = status =>
  ({
    active: 'teal',
    draft: 'amber',
    disabled: 'slate',
    archived: 'slate',
  })[status] || 'slate';

const documentKindOptions = computed(() => [
  {
    value: 'standard',
    label: t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.KIND_STANDARD'),
  },
  {
    value: 'openapi_spec',
    label: t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.KIND_OPENAPI'),
  },
]);
const knowledgeBaseStatusOptions = computed(() => [
  {
    value: 'active',
    label: t('CORTEX.KNOWLEDGE.STATUS.active'),
  },
  {
    value: 'inactive',
    label: t('CORTEX.KNOWLEDGE.STATUS.inactive'),
  },
]);
const knowledgeBaseStatusLabel = status =>
  knowledgeBaseStatusOptions.value.find(option => option.value === status)
    ?.label || status;
const knowledgeBaseStatusColor = status =>
  status === 'active' ? 'teal' : 'slate';
const documentPreviewMetrics = computed(() => {
  const summary = documentPreview.value?.summary || {};
  const labels = {
    added: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.METRICS.added'),
    changed: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.METRICS.changed'),
    removed: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.METRICS.removed'),
    unchanged: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.METRICS.unchanged'),
  };
  return Object.keys(labels).map(key => ({
    key,
    value: summary[key] || 0,
    label: labels[key],
  }));
});

const macroOptions = computed(() => {
  const options = macros.value.map(macro => ({
    value: macro.id,
    label: macro.name,
  }));
  return [
    { value: '', label: t('CORTEX.TOOLS.NATIVE.FORM.SELECT_MACRO') },
    ...options,
  ];
});

const automationRuleOptions = computed(() => {
  const options = automationRules.value.map(rule => ({
    value: rule.id,
    label: rule.name,
  }));
  return [
    { value: '', label: t('CORTEX.TOOLS.NATIVE.FORM.SELECT_AUTOMATION') },
    ...options,
  ];
});

const playgroundAgentOptions = computed(() => [
  { value: '', label: t('CORTEX.PLAYGROUND.FORM.NO_AGENT') },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

const knowledgeBaseOptions = computed(() =>
  knowledgeBases.value
    .filter(knowledgeBase => knowledgeBase.status === 'active')
    .map(knowledgeBase => ({
      value: knowledgeBase.id,
      label: knowledgeBase.name,
    }))
);

const selectedKnowledgeBase = computed(() =>
  knowledgeBases.value.find(
    knowledgeBase => knowledgeBase.id === selectedKnowledgeBaseId.value
  )
);

const toolHealthById = computed(() =>
  Object.fromEntries(
    toolHealthchecks.value.map(healthcheck => [
      healthcheck.tool_id,
      healthcheck,
    ])
  )
);
const toolHealthSummary = computed(() => ({
  online: toolHealthchecks.value.filter(item => item.status === 'online')
    .length,
  offline: toolHealthchecks.value.filter(item => item.status === 'offline')
    .length,
  attention: toolHealthchecks.value.filter(item =>
    ['unconfigured', 'disabled'].includes(item.status)
  ).length,
}));
const toolHealthStatusColor = status =>
  ({
    online: 'teal',
    offline: 'ruby',
    unconfigured: 'amber',
    disabled: 'slate',
  })[status] || 'slate';
const toolHealthStatusLabels = computed(() => ({
  online: t('CORTEX.TOOLS.HEALTH.STATUS.online'),
  offline: t('CORTEX.TOOLS.HEALTH.STATUS.offline'),
  unconfigured: t('CORTEX.TOOLS.HEALTH.STATUS.unconfigured'),
  disabled: t('CORTEX.TOOLS.HEALTH.STATUS.disabled'),
  pending: t('CORTEX.TOOLS.HEALTH.STATUS.pending'),
}));
const toolHealthErrorLabels = computed(() => ({
  inactive: t('CORTEX.TOOLS.HEALTH.ERROR.inactive'),
  healthcheck_url_missing: t(
    'CORTEX.TOOLS.HEALTH.ERROR.healthcheck_url_missing'
  ),
  resource_missing: t('CORTEX.TOOLS.HEALTH.ERROR.resource_missing'),
  timeout: t('CORTEX.TOOLS.HEALTH.ERROR.timeout'),
  connection_failed: t('CORTEX.TOOLS.HEALTH.ERROR.connection_failed'),
  request_failed: t('CORTEX.TOOLS.HEALTH.ERROR.request_failed'),
  http_error: t('CORTEX.TOOLS.HEALTH.ERROR.http_error'),
}));
const toolHealthStatusLabel = status =>
  toolHealthStatusLabels.value[status] || toolHealthStatusLabels.value.pending;
const toolHealthErrorLabel = error => toolHealthErrorLabels.value[error] || '';
const toolHealthCheckedAt = checkedAt =>
  new Intl.DateTimeFormat(undefined, { timeStyle: 'medium' }).format(
    new Date(checkedAt)
  );

const latestPlaygroundRun = computed(() => playgroundRuns.value[0] || null);
const playgroundRunHasResponse = run =>
  run?.output?.response_preview?.trim?.().length > 0;
const activePlaygroundSectionDetails = computed(() =>
  playgroundTabs.value.find(tab => tab.key === activePlaygroundSection.value)
);
const playgroundResultReviewState = run => {
  const decision = run.evaluation?.training?.decision;
  if (decision === 'correct') return 'correct';
  if (decision === 'corrected') return 'needs_correction';
  if (run.status === 'failed' || run.output?.response_error) return 'incorrect';
  return 'pending_review';
};
const playgroundResultReviewStateOptions = computed(() => [
  { value: 'all', label: t('CORTEX.PLAYGROUND.RESULTS.FILTERS.ALL') },
  {
    value: 'correct',
    label: t('CORTEX.PLAYGROUND.RESULTS.FILTERS.CORRECT'),
  },
  {
    value: 'incorrect',
    label: t('CORTEX.PLAYGROUND.RESULTS.FILTERS.INCORRECT'),
  },
  {
    value: 'needs_correction',
    label: t('CORTEX.PLAYGROUND.RESULTS.FILTERS.NEEDS_CORRECTION'),
  },
  {
    value: 'pending_review',
    label: t('CORTEX.PLAYGROUND.RESULTS.FILTERS.PENDING_REVIEW'),
  },
]);
const filteredPlaygroundRuns = computed(() => {
  const query = playgroundResultFilters.query.trim().toLocaleLowerCase();
  return playgroundRuns.value.filter(run => {
    const matchesReviewState =
      playgroundResultFilters.reviewState === 'all' ||
      playgroundResultReviewState(run) === playgroundResultFilters.reviewState;
    const searchableText = [run.input?.message, run.output?.response_preview]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase();
    return matchesReviewState && (!query || searchableText.includes(query));
  });
});
const paginatedPlaygroundRuns = computed(() => {
  const { page, per_page: perPage } = playgroundResultPagination.value;
  return filteredPlaygroundRuns.value.slice(
    (page - 1) * perPage,
    page * perPage
  );
});

const helpCenterPortalOptions = computed(() =>
  portals.value.map(portal => ({
    value: portal.id,
    label: portal.name,
  }))
);

const helpCenterLocaleOptions = computed(() => [
  {
    value: 'pt_BR',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt_BR'
    ),
  },
  {
    value: 'en',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.en'
    ),
  },
  {
    value: 'es',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.es'
    ),
  },
  {
    value: 'de',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.de'
    ),
  },
  {
    value: 'fr',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.fr'
    ),
  },
  {
    value: 'it',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.it'
    ),
  },
  {
    value: 'pt',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt'
    ),
  },
]);

const normalizedRecords = response =>
  response.data?.payload || response.data || [];

const defaultPortalLocale = portal =>
  portal.config?.default_locale ||
  portal.meta?.default_locale ||
  portal.default_locale ||
  portal.config?.allowed_locales?.[0]?.code ||
  'en';

const fetchHelpCenterCategories = async nextPortals => {
  const categoryRequests = nextPortals
    .filter(portal => portal.slug)
    .map(async portal => {
      const response = await categoriesAPI.get({
        portalSlug: portal.slug,
        locale: defaultPortalLocale(portal),
      });
      return normalizedRecords(response).map(category => ({
        ...category,
        portal_id: portal.id,
      }));
    });

  const results = await Promise.allSettled(categoryRequests);
  helpCenterCategories.value = results.flatMap(result =>
    result.status === 'fulfilled' ? result.value : []
  );
};

async function fetchAgents() {
  const [agentsResponse, knowledgeBasesResponse, portalsResponse] =
    await Promise.all([
      cortexAgentsAPI.get(),
      cortexKnowledgeBasesAPI.get(),
      new PortalsAPI().get(),
    ]);
  agents.value = normalizedRecords(agentsResponse);
  knowledgeBases.value = normalizedRecords(knowledgeBasesResponse);
  portals.value = normalizedRecords(portalsResponse);
  await fetchHelpCenterCategories(portals.value);
  if (!agents.value.some(agent => agent.id === selectedAgentId.value)) {
    selectedAgentId.value = agents.value[0]?.id || null;
  }
}

const fetchDocuments = async () => {
  if (!selectedKnowledgeBaseId.value) {
    documents.value = [];
    documentPagination.value.total = 0;
    return;
  }

  const response = await cortexKnowledgeBasesAPI.documents(
    selectedKnowledgeBaseId.value,
    {
      page: documentPagination.value.page,
      per_page: documentPagination.value.per_page,
      ...(documentSourceFilter.value
        ? { knowledge_source_id: documentSourceFilter.value.id }
        : {}),
    }
  );
  const records = normalizedRecords(response);
  documents.value = records;
  documentPagination.value.total = Number(
    response.headers?.['x-total-count'] || records.length
  );
};

const fetchKnowledgeSources = async () => {
  if (!selectedKnowledgeBaseId.value) {
    knowledgeSources.value = [];
    return;
  }

  const response = await cortexKnowledgeBasesAPI.sources(
    selectedKnowledgeBaseId.value
  );
  knowledgeSources.value = normalizedRecords(response);
};

const fetchKnowledgeDetails = async () => {
  await Promise.all([fetchDocuments(), fetchKnowledgeSources()]);
};

const fetchKnowledgeBases = async () => {
  const [knowledgeBasesResponse, portalsResponse] = await Promise.all([
    cortexKnowledgeBasesAPI.get(),
    new PortalsAPI().get(),
  ]);
  knowledgeBases.value = normalizedRecords(knowledgeBasesResponse);
  portals.value = normalizedRecords(portalsResponse);
  await fetchHelpCenterCategories(portals.value);
  if (
    !knowledgeBases.value.some(
      knowledgeBase => knowledgeBase.id === selectedKnowledgeBaseId.value
    )
  ) {
    selectedKnowledgeBaseId.value = knowledgeBases.value[0]?.id || null;
    documentPagination.value.page = 1;
    documentSourceFilter.value = null;
  }
  await fetchKnowledgeDetails();
};

const fetchToolMonitoring = async () => {
  try {
    const response = await cortexToolsAPI.monitoring({
      from: getUnixStartOfDay(toolMonitoringDateRange.value[0]),
      to: getUnixEndOfDay(toolMonitoringDateRange.value[1]),
    });
    toolMonitoring.value = response.data;
  } catch (error) {
    toolMonitoring.value = null;
    useAlert(t('CORTEX.TOOLS.HEALTH.API.ERROR'));
  }
};

const onToolMonitoringDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  toolMonitoringDateRange.value = [startDate, endDate];
  toolMonitoringRangeType.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchToolMonitoring();
};

let toolHealthPollingTimer;
const fetchToolHealth = async () => {
  if (isCheckingToolHealth.value) return;

  isCheckingToolHealth.value = true;
  try {
    const response = await cortexToolsAPI.health();
    toolHealthchecks.value = normalizedRecords(response);
    await fetchToolMonitoring();
  } catch (error) {
    useAlert(t('CORTEX.TOOLS.HEALTH.API.ERROR'));
  } finally {
    isCheckingToolHealth.value = false;
    clearTimeout(toolHealthPollingTimer);
    if (isToolHealthMonitoringEnabled.value && activeTab.value === 'tools') {
      toolHealthPollingTimer = setTimeout(
        fetchToolHealth,
        TOOL_HEALTH_POLL_INTERVAL
      );
    }
  }
};

const fetchTools = async () => {
  const [
    toolsResponse,
    externalCredentialsResponse,
    executionsResponse,
    macrosResponse,
    automationsResponse,
  ] = await Promise.all([
    cortexToolsAPI.get(),
    externalCredentialsAPI.list({ per_page: 100 }),
    cortexToolExecutionsAPI.get(),
    macrosAPI.get(),
    automationsAPI.get(),
  ]);
  tools.value = normalizedRecords(toolsResponse);
  externalCredentials.value = normalizedRecords(externalCredentialsResponse);
  toolExecutions.value = normalizedRecords(executionsResponse);
  macros.value = normalizedRecords(macrosResponse);
  automationRules.value = normalizedRecords(automationsResponse);
  try {
    const response = await attributesAPI.getAttributesByModel({
      attribute_model: 'contact_attribute',
    });
    contactCustomAttributes.value = normalizedRecords(response)
      .filter(attribute => attribute.attribute_key)
      .map(attribute => ({
        value: attribute.attribute_key,
        label: attribute.attribute_display_name || attribute.attribute_key,
      }));
  } catch {
    contactCustomAttributes.value = [];
  }
  if (isToolHealthMonitoringEnabled.value) {
    await fetchToolHealth();
  } else {
    await fetchToolMonitoring();
  }
};

const fetchPlaygroundRuns = async () => {
  const response = await cortexPlaygroundRunsAPI.get();
  playgroundRuns.value = normalizedRecords(response);
  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredPlaygroundRuns.value.length /
        playgroundResultPagination.value.per_page
    )
  );
  playgroundResultPagination.value.page = Math.min(
    playgroundResultPagination.value.page,
    totalPages
  );
};

const fetchPlayground = async () => {
  const [agentsResponse, knowledgeBasesResponse] = await Promise.all([
    cortexAgentsAPI.get(),
    cortexKnowledgeBasesAPI.get(),
  ]);
  agents.value = normalizedRecords(agentsResponse);
  knowledgeBases.value = normalizedRecords(knowledgeBasesResponse);
  if (
    !agents.value.some(agent => agent.id === selectedPlaygroundAgentId.value)
  ) {
    selectedPlaygroundAgentId.value = '';
  }
  await fetchPlaygroundRuns();
};

const fetchOverview = async () => {
  const [
    agentsResponse,
    knowledgeBasesResponse,
    toolsResponse,
    settingsResponse,
  ] = await Promise.all([
    cortexAgentsAPI.get(),
    cortexKnowledgeBasesAPI.get(),
    cortexToolsAPI.get(),
    cortexSettingsAPI.get(),
  ]);
  agents.value = normalizedRecords(agentsResponse);
  knowledgeBases.value = normalizedRecords(knowledgeBasesResponse);
  tools.value = normalizedRecords(toolsResponse);
  cortexSettings.value = settingsResponse.data;
};

const fetchAuditEvents = async () => {
  const response = await cortexAuditEventsAPI.get();
  auditEvents.value = normalizedRecords(response);
};

const scrollToElement = target => {
  if (!target) return;

  document.getElementById(target)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

async function scrollToActiveQuerySection() {
  const section = route.query?.section;

  if (
    activeTab.value === 'agents' &&
    ['agent_knowledge', 'agent_status', 'inboxes'].includes(section) &&
    selectedAgent.value
  ) {
    openEditWizard(selectedAgent.value);
    return;
  }

  const target = sectionTargetByQuery[section];
  if (!target) return;

  if (
    activeTab.value === 'knowledge' &&
    knowledgeSectionLinks.value.some(item => item.key === section)
  ) {
    activeKnowledgeSection.value = section;
    await nextTick();
  }

  if (activeTab.value === 'tools') {
    const toolSectionByQuery = {
      tool_create: 'create',
      tool_executions: 'executions',
      tool_native: 'native',
      tool_health: 'health',
    };
    const toolSection = toolSectionByQuery[section];
    if (toolSection) {
      activeToolSection.value = toolSection;
      await nextTick();
    }
  }

  scrollToElement(target);
}

const fetchActiveTab = async () => {
  isLoading.value = true;
  try {
    if (activeTab.value === 'overview') {
      await fetchOverview();
    } else if (activeTab.value === 'agents') {
      await fetchAgents();
    } else if (activeTab.value === 'knowledge') {
      await fetchKnowledgeBases();
    } else if (activeTab.value === 'tools') {
      await fetchTools();
    } else if (activeTab.value === 'playground') {
      await fetchPlayground();
    } else if (activeTab.value === 'audit') {
      await fetchAuditEvents();
    }
  } catch (error) {
    useAlert(t('CORTEX.API.ERROR'));
  } finally {
    isLoading.value = false;
    await nextTick();
    await scrollToActiveQuerySection();
  }
};

const handleWizardCreated = async () => {
  showAgentWizard.value = false;
  editingAgent.value = null;
  await fetchAgents();
};

const handleWizardUpdated = async () => {
  showAgentWizard.value = false;
  editingAgent.value = null;
  await fetchAgents();
};

const requestDestructiveAction = (description, action) => {
  destructiveActionDescription.value = description;
  pendingDestructiveAction.value = action;
  destructiveActionDialog.value?.open();
};

const confirmDestructiveAction = async () => {
  if (!pendingDestructiveAction.value) return;

  isRunningDestructiveAction.value = true;
  try {
    await pendingDestructiveAction.value();
    destructiveActionDialog.value?.close();
  } finally {
    isRunningDestructiveAction.value = false;
  }
};

const clearDestructiveAction = () => {
  destructiveActionDescription.value = '';
  pendingDestructiveAction.value = null;
};

const executeDeleteSelectedAgent = async () => {
  isDeletingAgent.value = true;
  try {
    await cortexAgentsAPI.delete(selectedAgent.value.id);
    useAlert(t('CORTEX.AGENTS.API.DELETED'));
    await fetchAgents();
  } catch (error) {
    useAlert(t('CORTEX.AGENTS.API.DELETE_ERROR'));
  } finally {
    isDeletingAgent.value = false;
  }
};

const deleteSelectedAgent = () => {
  if (!selectedAgent.value) return;

  requestDestructiveAction(
    t('CORTEX.AGENTS.ACTIONS.DELETE_CONFIRM', {
      name: selectedAgent.value.name,
    }),
    executeDeleteSelectedAgent
  );
};

const agentStatusLabel = status => {
  return (
    agentStatusOptions.value.find(option => option.value === status)?.label ||
    status
  );
};
const agentStatusColor = status =>
  ({
    active: 'teal',
    testing: 'amber',
    draft: 'slate',
    paused: 'amber',
    archived: 'slate',
  })[status] || 'slate';

const agentActiveInboxCount = agent => Number(agent?.active_inboxes_count) || 0;
const agentInboxCountLabel = agent =>
  t('CORTEX.AGENTS.INBOX_COUNT', {
    count: agentActiveInboxCount(agent),
  });

const createKnowledgeBase = async knowledgeBase => {
  isSavingKnowledgeBase.value = true;
  try {
    const { data } = await cortexKnowledgeBasesAPI.create({
      knowledge_base: knowledgeBase,
    });
    selectedKnowledgeBaseId.value = data.id;
    documentPagination.value.page = 1;
    documentSourceFilter.value = null;
    showKnowledgeBaseDialog.value = false;
    useAlert(t('CORTEX.KNOWLEDGE.API.CREATED'));
    await fetchKnowledgeBases();
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.API.ERROR'));
  } finally {
    isSavingKnowledgeBase.value = false;
  }
};

const selectKnowledgeBase = async knowledgeBase => {
  selectedKnowledgeBaseId.value = knowledgeBase.id;
  documentPagination.value.page = 1;
  documentSourceFilter.value = null;
  activeKnowledgeSection.value = 'documents';
  await fetchKnowledgeDetails();
};

const selectKnowledgeSection = section => {
  activeKnowledgeSection.value = section.key;
};

const sourceFilterValues = (configuration, pluralKey, singularKey) => {
  const value = configuration?.[pluralKey] ?? configuration?.[singularKey];
  return (Array.isArray(value) ? value : [value])
    .filter(item => item !== undefined && item !== null && item !== '')
    .map(item => (pluralKey === 'portal_ids' ? Number(item) : String(item)));
};

const configureHelpCenterSource = async source => {
  helpCenterForm.portal_ids = sourceFilterValues(
    source.sync_config,
    'portal_ids',
    'portal_id'
  );
  helpCenterForm.locales = sourceFilterValues(
    source.sync_config,
    'locales',
    'locale'
  );
  activeKnowledgeSection.value = 'help_center';
  await nextTick();
  scrollToElement('cortex-knowledge-help-center');
};

const viewSourceDocuments = async source => {
  documentSourceFilter.value = { id: source.id, name: source.name };
  documentPagination.value.page = 1;
  activeKnowledgeSection.value = 'documents';
  await fetchDocuments();
  await nextTick();
  scrollToElement('cortex-knowledge-documents');
};

const clearDocumentSourceFilter = async () => {
  documentSourceFilter.value = null;
  documentPagination.value.page = 1;
  await fetchDocuments();
};

const selectPlaygroundSection = section => {
  activePlaygroundSection.value = section.key;
};

const selectToolSection = section => {
  activeToolSection.value = section.key;
};

const changeNativeToolPage = page => {
  nativeToolPagination.value.page = page;
};

const changeNativeToolPerPage = perPage => {
  nativeToolPagination.value.per_page = perPage;
  nativeToolPagination.value.page = 1;
};

const changeDocumentPage = page => {
  documentPagination.value.page = page;
  fetchDocuments();
};

const changeDocumentPerPage = perPage => {
  documentPagination.value.per_page = perPage;
  if (documentPagination.value.page === 1) fetchDocuments();
};

const applyPlaygroundResultFilters = () => {
  playgroundResultPagination.value.page = 1;
};

const changePlaygroundResultPage = page => {
  playgroundResultPagination.value.page = page;
};

const changePlaygroundResultPerPage = perPage => {
  playgroundResultPagination.value.per_page = perPage;
  playgroundResultPagination.value.page = 1;
};

const editKnowledgeBase = async knowledgeBase => {
  await selectKnowledgeBase(knowledgeBase);
  editingKnowledgeBase.value = selectedKnowledgeBase.value;
  showKnowledgeBaseDialog.value = true;
};

const updateSelectedKnowledgeBase = async knowledgeBase => {
  if (!editingKnowledgeBase.value) return;

  isUpdatingKnowledgeBase.value = true;
  try {
    await cortexKnowledgeBasesAPI.update(editingKnowledgeBase.value.id, {
      knowledge_base: knowledgeBase,
    });
    showKnowledgeBaseDialog.value = false;
    editingKnowledgeBase.value = null;
    useAlert(t('CORTEX.KNOWLEDGE.API.UPDATED'));
    await fetchKnowledgeBases();
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.API.UPDATE_ERROR'));
  } finally {
    isUpdatingKnowledgeBase.value = false;
  }
};

const saveKnowledgeBase = knowledgeBase => {
  if (editingKnowledgeBase.value) {
    updateSelectedKnowledgeBase(knowledgeBase);
  } else {
    createKnowledgeBase(knowledgeBase);
  }
};

const executeDeleteSelectedKnowledgeBase = async () => {
  isDeletingKnowledgeBase.value = true;
  try {
    await cortexKnowledgeBasesAPI.delete(selectedKnowledgeBase.value.id);
    useAlert(t('CORTEX.KNOWLEDGE.API.DELETED'));
    await fetchKnowledgeBases();
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.API.DELETE_ERROR'));
  } finally {
    isDeletingKnowledgeBase.value = false;
  }
};

const deleteSelectedKnowledgeBase = () => {
  if (!selectedKnowledgeBase.value) return;

  requestDestructiveAction(
    t('CORTEX.KNOWLEDGE.ACTIONS.DELETE_CONFIRM', {
      name: selectedKnowledgeBase.value.name,
    }),
    executeDeleteSelectedKnowledgeBase
  );
};

const parseJsonField = (value, fallback) => {
  if (!value.trim()) return fallback;

  return JSON.parse(value);
};

const structuredImportPayload = () => ({
  source_name:
    structuredImportForm.source_name ||
    t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.DEFAULT_SOURCE_NAME'),
  rows: parseJsonField(structuredImportForm.rows, []),
  schema: parseJsonField(structuredImportForm.schema, {}),
});

const syncHelpCenter = async () => {
  if (!selectedKnowledgeBase.value) return;

  isSyncingHelpCenter.value = true;
  try {
    await cortexKnowledgeBasesAPI.syncHelpCenter(
      selectedKnowledgeBase.value.id,
      {
        portal_ids: helpCenterForm.portal_ids.length
          ? helpCenterForm.portal_ids
          : undefined,
        locales: helpCenterForm.locales.length
          ? helpCenterForm.locales
          : undefined,
      }
    );
    useAlert(t('CORTEX.KNOWLEDGE.HELP_CENTER.API.SYNCED'));
    await fetchKnowledgeDetails();
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.HELP_CENTER.API.ERROR'));
  } finally {
    isSyncingHelpCenter.value = false;
  }
};

const previewStructuredImport = async () => {
  if (!selectedKnowledgeBase.value) return;

  isPreviewingStructuredImport.value = true;
  try {
    const { data } = await cortexKnowledgeBasesAPI.previewStructured(
      selectedKnowledgeBase.value.id,
      structuredImportPayload()
    );
    structuredImportPreview.value = data;
    useAlert(t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.API.PREVIEWED'));
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.API.ERROR'));
  } finally {
    isPreviewingStructuredImport.value = false;
  }
};

const importStructured = async () => {
  if (!selectedKnowledgeBase.value) return;

  isImportingStructured.value = true;
  try {
    await cortexKnowledgeBasesAPI.importStructured(
      selectedKnowledgeBase.value.id,
      structuredImportPayload()
    );
    structuredImportForm.source_name = '';
    structuredImportForm.rows = '';
    structuredImportForm.schema = '';
    structuredImportPreview.value = null;
    useAlert(t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.API.IMPORTED'));
    await fetchKnowledgeDetails();
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.API.ERROR'));
  } finally {
    isImportingStructured.value = false;
  }
};

const handleDocumentFileChange = event => {
  selectedDocumentFile.value = event.target.files?.[0] || null;
  documentPreview.value = null;
};

const documentFormData = () => {
  const formData = new FormData();
  formData.append('document[name]', documentForm.name);
  formData.append('document[document_type]', 'uploaded_file');
  if (documentForm.document_kind === 'openapi_spec') {
    formData.append('document[document_kind]', 'openapi_spec');
    formData.append('document[source_key]', 'openapi_spec');
  }
  formData.append('file', selectedDocumentFile.value);
  return formData;
};

const previewDocument = async () => {
  if (!selectedKnowledgeBase.value || !selectedDocumentFile.value) return;

  isPreviewingDocument.value = true;
  try {
    const { data } = await cortexKnowledgeBasesAPI.previewDocument(
      selectedKnowledgeBase.value.id,
      documentFormData()
    );
    documentPreview.value = data;
  } catch (error) {
    documentPreview.value = null;
    useAlert(t('CORTEX.KNOWLEDGE.DOCUMENTS.API.PREVIEW_ERROR'));
  } finally {
    isPreviewingDocument.value = false;
  }
};

const createDocument = async () => {
  if (!selectedKnowledgeBase.value || !selectedDocumentFile.value) return;

  isSavingDocument.value = true;
  try {
    await cortexKnowledgeBasesAPI.createDocument(
      selectedKnowledgeBase.value.id,
      documentFormData()
    );
    documentForm.name = '';
    documentForm.document_kind = 'standard';
    selectedDocumentFile.value = null;
    if (documentFileInputRef.value) {
      documentFileInputRef.value.value = '';
    }
    documentPreview.value = null;
    useAlert(t('CORTEX.KNOWLEDGE.DOCUMENTS.API.CREATED'));
    documentPagination.value.page = 1;
    await fetchDocuments();
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.DOCUMENTS.API.ERROR'));
  } finally {
    isSavingDocument.value = false;
  }
};

const executeDeleteDocument = async document => {
  deletingDocumentId.value = document.id;
  try {
    await cortexKnowledgeBasesAPI.deleteDocument(
      selectedKnowledgeBase.value.id,
      document.id
    );
    useAlert(t('CORTEX.KNOWLEDGE.DOCUMENTS.API.DELETED'));
    await fetchDocuments();
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.DOCUMENTS.API.DELETE_ERROR'));
  } finally {
    deletingDocumentId.value = null;
  }
};

const deleteDocument = document => {
  if (!selectedKnowledgeBase.value) return;

  requestDestructiveAction(
    t('CORTEX.KNOWLEDGE.DOCUMENTS.ACTIONS.DELETE_CONFIRM', {
      name: document.name,
    }),
    () => executeDeleteDocument(document)
  );
};

const fetchDocumentContent = async page => {
  if (!selectedKnowledgeBase.value || !viewedDocument.value) return;

  isLoadingDocumentContent.value = true;
  try {
    const { data } = await cortexKnowledgeBasesAPI.showDocument(
      selectedKnowledgeBase.value.id,
      viewedDocument.value.id,
      {
        content_page: page,
        content_per_page: documentContentPagination.value.per_page,
      }
    );
    viewedDocument.value = data;
    viewedDocumentChunks.value = data.content?.chunks || [];
    documentContentPagination.value = {
      page: data.content?.pagination?.page || 1,
      per_page: data.content?.pagination?.per_page || 10,
      total: data.content?.pagination?.total || 0,
    };
  } catch (error) {
    useAlert(t('CORTEX.KNOWLEDGE.DOCUMENTS.API.VIEW_ERROR'));
  } finally {
    isLoadingDocumentContent.value = false;
  }
};

const viewDocument = async document => {
  viewedDocument.value = document;
  viewedDocumentChunks.value = [];
  documentContentPagination.value = { page: 1, per_page: 10, total: 0 };
  documentViewerDialog.value?.open?.();
  await fetchDocumentContent(1);
};

const clearDocumentViewer = () => {
  viewedDocument.value = null;
  viewedDocumentChunks.value = [];
  documentContentPagination.value = { page: 1, per_page: 10, total: 0 };
};

const createTool = async tool => {
  isSavingTool.value = true;
  try {
    if (editingTool.value?.id) {
      await cortexToolsAPI.update(editingTool.value.id, { tool });
    } else {
      await cortexToolsAPI.create({ tool });
    }
    showToolDialog.value = false;
    editingTool.value = null;
    useAlert(t('CORTEX.TOOLS.API.CREATED'));
    await fetchTools();
  } catch (error) {
    useAlert(t('CORTEX.TOOLS.API.ERROR'));
  } finally {
    isSavingTool.value = false;
  }
};

const openCreateToolDialog = () => {
  editingTool.value = null;
  showToolDialog.value = true;
};

const openEditToolDialog = tool => {
  editingTool.value = tool;
  showToolDialog.value = true;
};

const closeToolContextMenu = () => {
  toolContextMenu.value = null;
};

const openToolContextMenu = (event, tool) => {
  event.preventDefault();
  toolContextMenuPosition.x = event.pageX || event.clientX;
  toolContextMenuPosition.y = event.pageY || event.clientY;
  toolContextMenu.value = tool;
};

const editToolFromContextMenu = () => {
  const tool = toolContextMenu.value;
  closeToolContextMenu();
  if (tool) openEditToolDialog(tool);
};

const duplicateTool = () => {
  const tool = toolContextMenu.value;
  closeToolContextMenu();
  if (!tool) return;

  editingTool.value = {
    ...tool,
    id: null,
    name: t('CORTEX.TOOLS.FORM.DUPLICATE_NAME', { name: tool.name }),
    status: 'draft',
  };
  showToolDialog.value = true;
};

const openToolTestDialog = payload => {
  toolTestPayload.value = payload;
};

const runToolTest = async templateValues => {
  if (!toolTestPayload.value) return;

  isTestingTool.value = true;
  try {
    const { data } = await cortexToolsAPI.test({
      tool: toolTestPayload.value.tool,
      template_values: templateValues,
    });
    toolTestPayload.value = null;
    toolTestResult.value = data.response;
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('CORTEX.TOOLS.API.TEST_ERROR')
    );
  } finally {
    isTestingTool.value = false;
  }
};

const executeDeleteTool = async tool => {
  isDeletingToolId.value = tool.id;
  try {
    await cortexToolsAPI.delete(tool.id);
    useAlert(t('CORTEX.TOOLS.API.DELETED'));
    await fetchTools();
  } catch (error) {
    useAlert(t('CORTEX.TOOLS.API.DELETE_ERROR'));
  } finally {
    isDeletingToolId.value = null;
  }
};

const deleteTool = tool => {
  requestDestructiveAction(
    t('CORTEX.TOOLS.FORM.DELETE_CONFIRM', { name: tool.name }),
    () => executeDeleteTool(tool)
  );
};

const deleteToolFromContextMenu = () => {
  const tool = toolContextMenu.value;
  closeToolContextMenu();
  if (tool) deleteTool(tool);
};

const createNativeTool = async sourceType => {
  isSavingNativeTool.value = true;
  try {
    if (sourceType === 'macro') {
      await cortexToolsAPI.fromMacro({ macro_id: nativeToolForm.macro_id });
      nativeToolForm.macro_id = '';
    } else if (sourceType === 'automation') {
      await cortexToolsAPI.fromAutomation({
        automation_rule_id: nativeToolForm.automation_rule_id,
      });
      nativeToolForm.automation_rule_id = '';
    }
    useAlert(t('CORTEX.TOOLS.NATIVE.API.CREATED'));
    await fetchTools();
  } catch (error) {
    useAlert(t('CORTEX.TOOLS.NATIVE.API.ERROR'));
  } finally {
    isSavingNativeTool.value = false;
  }
};

const updateToolExecution = async (execution, action) => {
  isUpdatingToolExecution.value = true;
  try {
    if (action === 'approve') {
      await cortexToolExecutionsAPI.approve(execution.id);
      useAlert(t('CORTEX.TOOLS.EXECUTIONS.API.APPROVED'));
    } else if (action === 'reject') {
      await cortexToolExecutionsAPI.reject(execution.id);
      useAlert(t('CORTEX.TOOLS.EXECUTIONS.API.REJECTED'));
    }
    await fetchTools();
  } catch (error) {
    useAlert(t('CORTEX.TOOLS.EXECUTIONS.API.ERROR'));
  } finally {
    isUpdatingToolExecution.value = false;
  }
};

const runPlayground = async () => {
  isRunningPlayground.value = true;
  try {
    await cortexPlaygroundRunsAPI.create({
      input: {
        message: playgroundForm.message,
        agent_id: selectedPlaygroundAgentId.value || null,
      },
    });
    playgroundForm.message = '';
    useAlert(t('CORTEX.PLAYGROUND.API.CREATED'));
    await fetchPlaygroundRuns();
  } catch (error) {
    useAlert(t('CORTEX.PLAYGROUND.API.ERROR'));
  } finally {
    isRunningPlayground.value = false;
  }
};

const startPlaygroundTraining = run => {
  if (!playgroundRunHasResponse(run)) return;

  playgroundTraining.runId = run.id;
  playgroundTraining.answer = run.output?.response_preview || '';
  playgroundTraining.knowledgeBaseId =
    run.trace?.knowledge_results?.[0]?.knowledge_base_id || '';
};

const updatePlaygroundAnswer = (run, answer) => {
  if (!run) return;

  if (playgroundTraining.runId !== run.id) {
    startPlaygroundTraining(run);
  }
  playgroundTraining.answer = answer;
};

const savePlaygroundTraining = async decision => {
  isSavingPlaygroundTraining.value = true;
  try {
    await cortexPlaygroundRunsAPI.train(playgroundTraining.runId, {
      training: {
        decision,
        answer: playgroundTraining.answer,
        knowledge_base_id: playgroundTraining.knowledgeBaseId || null,
      },
    });
    useAlert(t('CORTEX.PLAYGROUND.TRAINING.SAVED'));
    playgroundTraining.runId = null;
    await fetchPlaygroundRuns();
  } catch (error) {
    useAlert(t('CORTEX.PLAYGROUND.TRAINING.ERROR'));
  } finally {
    isSavingPlaygroundTraining.value = false;
  }
};

const markPlaygroundCorrect = async run => {
  if (!playgroundRunHasResponse(run)) return;

  startPlaygroundTraining(run);
  await savePlaygroundTraining('correct');
};

const retestPlaygroundRun = async run => {
  isRetestingPlaygroundRunId.value = run.id;
  try {
    await cortexPlaygroundRunsAPI.retest(run.id);
    useAlert(t('CORTEX.PLAYGROUND.REGRESSION.CREATED'));
    await fetchPlaygroundRuns();
  } catch (error) {
    useAlert(t('CORTEX.PLAYGROUND.REGRESSION.ERROR'));
  } finally {
    isRetestingPlaygroundRunId.value = null;
  }
};

const playgroundCheckLabel = key => {
  const labels = {
    has_active_agent: t('CORTEX.PLAYGROUND.CHECKS.ACTIVE_AGENT'),
    has_scenarios: t('CORTEX.PLAYGROUND.CHECKS.SCENARIOS'),
    has_tools: t('CORTEX.PLAYGROUND.CHECKS.TOOLS'),
    has_knowledge_match: t('CORTEX.PLAYGROUND.CHECKS.KNOWLEDGE'),
  };
  return labels[key] || key;
};

const playgroundStatusLabel = status =>
  ({
    queued: t('CORTEX.PLAYGROUND.STATUS.QUEUED'),
    running: t('CORTEX.PLAYGROUND.STATUS.RUNNING'),
    succeeded: t('CORTEX.PLAYGROUND.STATUS.SUCCEEDED'),
    failed: t('CORTEX.PLAYGROUND.STATUS.FAILED'),
  })[status] || t('CORTEX.PLAYGROUND.STATUS.UNKNOWN');

const documentPreviewStatusLabel = status =>
  ({
    added: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.STATUS.added'),
    changed: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.STATUS.changed'),
    removed: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.STATUS.removed'),
    unchanged: t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.STATUS.unchanged'),
  })[status] || status;

const documentPreviewStatusColor = status =>
  ({
    added: 'teal',
    changed: 'amber',
    removed: 'ruby',
    unchanged: 'slate',
  })[status] || 'slate';

const documentStatusLabel = status =>
  ({
    pending: t('CORTEX.KNOWLEDGE.DOCUMENTS.STATUS.pending'),
    processing: t('CORTEX.KNOWLEDGE.DOCUMENTS.STATUS.processing'),
    ready: t('CORTEX.KNOWLEDGE.DOCUMENTS.STATUS.ready'),
    failed: t('CORTEX.KNOWLEDGE.DOCUMENTS.STATUS.failed'),
    archived: t('CORTEX.KNOWLEDGE.DOCUMENTS.STATUS.archived'),
  })[status] || status;

const documentStatusColor = status =>
  ({
    ready: 'teal',
    processing: 'amber',
    failed: 'ruby',
    archived: 'slate',
    pending: 'slate',
  })[status] || 'slate';

const DOCUMENT_FILE_TYPE_BADGES = {
  txt: { color: 'slate', icon: 'i-lucide-file-text' },
  md: { color: 'slate', icon: 'i-lucide-file-text' },
  json: { color: 'iris', icon: 'i-lucide-braces' },
  yaml: { color: 'iris', icon: 'i-lucide-braces' },
  yml: { color: 'iris', icon: 'i-lucide-braces' },
  xml: { color: 'iris', icon: 'i-lucide-code-xml' },
  html: { color: 'iris', icon: 'i-lucide-code-xml' },
  csv: { color: 'teal', icon: 'i-lucide-table-2' },
  xlsx: { color: 'teal', icon: 'i-lucide-table-2' },
  pdf: { color: 'ruby', icon: 'i-lucide-file-text' },
  docx: { color: 'blue', icon: 'i-lucide-file-type' },
  pptx: { color: 'amber', icon: 'i-lucide-presentation' },
  png: { color: 'brand', icon: 'i-lucide-image' },
  jpg: { color: 'brand', icon: 'i-lucide-image' },
  jpeg: { color: 'brand', icon: 'i-lucide-image' },
  webp: { color: 'brand', icon: 'i-lucide-image' },
  mp3: { color: 'amber', icon: 'i-lucide-audio-lines' },
  wav: { color: 'amber', icon: 'i-lucide-audio-lines' },
  ogg: { color: 'amber', icon: 'i-lucide-audio-lines' },
  m4a: { color: 'amber', icon: 'i-lucide-audio-lines' },
  mp4: { color: 'blue', icon: 'i-lucide-video' },
  mov: { color: 'blue', icon: 'i-lucide-video' },
  webm: { color: 'blue', icon: 'i-lucide-video' },
};

const documentFileTypeBadge = fileType => {
  const normalizedType = fileType?.toString().toLowerCase() || '';
  return {
    label: normalizedType.toUpperCase(),
    ...(DOCUMENT_FILE_TYPE_BADGES[normalizedType] || {
      color: 'slate',
      icon: 'i-lucide-file',
    }),
  };
};

const documentDiffLineClass = type =>
  ({
    added: 'bg-n-teal-2 text-n-teal-12',
    removed: 'bg-n-ruby-2 text-n-ruby-12',
    context: 'text-n-slate-11',
  })[type] || 'text-n-slate-11';

const documentDiffLinePrefix = type =>
  ({
    added: '+ ',
    removed: '- ',
    context: '  ',
  })[type] || '  ';

const tabRoute = tab =>
  accountScopedRoute('cortex_index', { navigationPath: tab.key });

const handleTabChanged = tab => {
  router.push(tabRoute(tab));
};

const prepareCortexTour = async () => {
  if (activeTab.value !== 'overview') {
    await router.push(
      accountScopedRoute('cortex_index', { navigationPath: 'overview' })
    );
  }
  await nextTick();
};

const startPendingCortexTour = async () => {
  if (!canManageCortex.value || activeTab.value !== 'overview') return;

  await nextTick();
  startCortexTourIfPending();
};

watch(activeTab, async () => {
  await fetchActiveTab();
  await startPendingCortexTour();
});
watch(
  () => route.query?.section,
  async () => {
    await nextTick();
    await scrollToActiveQuerySection();
  }
);
watch(isToolHealthMonitoringEnabled, enabled => {
  if (enabled && activeTab.value === 'tools') {
    fetchToolHealth();
  } else {
    clearTimeout(toolHealthPollingTimer);
  }
});
onMounted(async () => {
  await fetchActiveTab();
  // Sempre carregar bases de conhecimento para o dropdown no formulário de agentes
  if (activeTab.value === 'agents') {
    await fetchKnowledgeBases();
  }
  await startPendingCortexTour();
});
onUnmounted(() => clearTimeout(toolHealthPollingTimer));
</script>

<template>
  <main class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <ModuleSettingsLayout
      :title="t('CORTEX.HEADER.TITLE')"
      :description="t('CORTEX.HEADER.DESCRIPTION')"
    >
      <template v-if="canManageCortex" #actions>
        <ResourceHelpActions
          id="cortex-help-actions"
          :system-key="cortexHelpSystemKey"
          :article-key="cortexHelpArticleKey"
          :before-start="prepareCortexTour"
        />
      </template>

      <template #tabs>
        <div id="cortex-navigation" class="grid justify-start gap-2">
          <TabBar
            :tabs="primaryTabs"
            :initial-active-tab="primaryActiveTabIndex"
            @tab-changed="handleTabChanged"
          />
          <TabBar
            v-if="secondaryTabs.length"
            :tabs="secondaryTabs"
            :initial-active-tab="secondaryActiveTabIndex"
            @tab-changed="handleTabChanged"
          />
        </div>
      </template>

      <woot-loading-state v-if="isLoading" />

      <section v-else-if="activeTab === 'overview'" class="grid gap-6">
        <div
          class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]"
        >
          <section class="grid gap-4">
            <div>
              <h2 class="font-medium text-n-slate-12">
                {{ t('CORTEX.OVERVIEW.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('CORTEX.OVERVIEW.DESCRIPTION') }}
              </p>
            </div>

            <div id="cortex-overview-status" class="grid gap-3 md:grid-cols-3">
              <article
                v-for="stat in overviewStats"
                :key="stat.key"
                class="grid h-28 grid-rows-2 overflow-hidden border border-n-weak rounded-lg"
              >
                <div class="flex items-center gap-2 px-4">
                  <span
                    class="flex items-center justify-center size-7 rounded-md bg-n-alpha-2 text-n-brand shrink-0"
                  >
                    <span class="size-4" :class="stat.icon" />
                  </span>
                  <p class="text-sm leading-5 text-n-slate-11 line-clamp-2">
                    {{ stat.label }}
                  </p>
                </div>
                <div class="flex items-center px-4">
                  <p class="text-2xl font-semibold text-n-slate-12">
                    {{ stat.value }}
                  </p>
                </div>
              </article>
            </div>

            <div class="grid gap-3 md:grid-cols-2">
              <article
                v-for="card in overviewCards"
                :id="`cortex-overview-group-${card.key}`"
                :key="card.key"
                class="p-4 border border-n-weak rounded-lg"
              >
                <div class="flex items-start gap-3">
                  <span
                    class="flex items-center justify-center size-9 rounded-lg bg-n-alpha-2 text-n-brand shrink-0 aspect-square"
                  >
                    <span
                      class="size-4 shrink-0 aspect-square"
                      :class="card.icon"
                    />
                  </span>
                  <div class="min-w-0">
                    <h3 class="font-medium text-n-slate-12">
                      {{ card.title }}
                    </h3>
                    <p class="mt-1 text-sm text-n-slate-11">
                      {{ card.description }}
                    </p>
                    <NextButton
                      faded
                      slate
                      xs
                      class="mt-3"
                      @click="goToCortexSection(card.route, card.section)"
                    >
                      {{ card.action }}
                    </NextButton>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <aside class="grid content-start gap-3">
            <section
              id="cortex-overview-checklist"
              class="p-4 border border-n-weak rounded-lg"
            >
              <h2 class="font-medium text-n-slate-12">
                {{ t('CORTEX.OVERVIEW.CHECKLIST_TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.OVERVIEW.CHECKLIST_DESCRIPTION') }}
              </p>
              <div class="grid gap-2 mt-4">
                <button
                  v-for="item in overviewChecklist"
                  :key="item.key"
                  type="button"
                  class="flex items-center justify-between gap-3 p-3 text-start border border-n-weak rounded-lg hover:bg-n-alpha-2"
                  @click="goToCortexSection(item.route, item.section)"
                >
                  <span class="min-w-0">
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{ item.label }}
                    </span>
                    <span class="block mt-1 text-xs text-n-slate-11">
                      {{ item.description }}
                    </span>
                  </span>
                  <span class="flex items-center gap-2 shrink-0">
                    <Label
                      :label="overviewChecklistStatusLabel(item.status)"
                      :color="overviewChecklistStatusColor(item.status)"
                      compact
                    />
                    <span
                      class="i-lucide-arrow-right size-4 text-n-slate-10 shrink-0"
                    />
                  </span>
                </button>
              </div>
            </section>
          </aside>
        </div>
      </section>

      <CortexSectionGuide
        v-else-if="activeTab === 'agents'"
        :title="t('CORTEX.AGENTS.LIST_TITLE')"
        :description="t('CORTEX.AGENTS.LIST_DESCRIPTION')"
        :sections="agentSections"
        active-section="agents"
      >
        <template #actions>
          <NextButton
            sm
            @click="
              editingAgent = null;
              showAgentWizard = true;
            "
          >
            <span class="i-lucide-plus size-4" />
            {{ t('CORTEX.AGENTS.FORM.SUBMIT') }}
          </NextButton>
        </template>

        <div
          v-if="agents.length === 0"
          class="text-center py-12 border border-dashed border-n-weak rounded-lg bg-n-surface-1"
        >
          <div
            class="flex items-center justify-center size-16 rounded-lg bg-n-brand/10 mx-auto mb-4"
          >
            <span class="i-lucide-wand-sparkles size-8 text-n-brand" />
          </div>
          <h3 class="text-base font-medium text-n-slate-12 mb-1">
            {{ t('CORTEX.AGENTS.EMPTY_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ t('CORTEX.AGENTS.EMPTY_DESC') }}
          </p>
          <NextButton
            blue
            class="mx-auto"
            @click="
              editingAgent = null;
              showAgentWizard = true;
            "
          >
            <span class="i-lucide-plus size-4" />
            {{ t('CORTEX.AGENTS.FORM.SUBMIT') }}
          </NextButton>
        </div>

        <div v-else class="grid gap-5">
          <SettingsListGroup class="grid content-start">
            <article
              v-for="agent in agents"
              :key="agent.id"
              class="grid gap-3 px-4 py-3 transition-colors hover:bg-n-alpha-1 md:grid-cols-[minmax(0,1fr)_auto]"
            >
              <div class="min-w-0 text-left">
                <div class="flex items-start gap-3">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <h2 class="font-medium text-n-slate-12">
                        {{ agent.name }}
                      </h2>
                      <Label
                        :label="agentStatusLabel(agent.status)"
                        :color="agentStatusColor(agent.status)"
                        compact
                      >
                        <template #icon>
                          <span class="i-lucide-circle size-2.5 fill-current" />
                        </template>
                      </Label>
                      <Label
                        :label="agentInboxCountLabel(agent)"
                        color="slate"
                        compact
                      />
                    </div>
                    <p class="mt-1 text-sm text-n-slate-11">
                      {{
                        agent.description || t('CORTEX.AGENTS.NO_DESCRIPTION')
                      }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2 shrink-0 md:justify-end">
                <NextButton
                  type="button"
                  sm
                  slate
                  ghost
                  icon="i-lucide-pencil"
                  :aria-label="t('CORTEX.AGENTS.ACTIONS.EDIT')"
                  :title="t('CORTEX.AGENTS.ACTIONS.EDIT')"
                  @click="openEditWizard(agent)"
                />
                <NextButton
                  type="button"
                  sm
                  ruby
                  ghost
                  icon="i-lucide-trash-2"
                  :aria-label="t('CORTEX.AGENTS.ACTIONS.DELETE')"
                  :title="t('CORTEX.AGENTS.ACTIONS.DELETE')"
                  :is-loading="isDeletingAgent && selectedAgentId === agent.id"
                  @click="
                    selectedAgentId = agent.id;
                    deleteSelectedAgent();
                  "
                />
              </div>
            </article>
            <p v-if="!agents.length" class="text-sm text-n-slate-11">
              {{ t('CORTEX.AGENTS.EMPTY') }}
            </p>
          </SettingsListGroup>
        </div>

        <CortexAgentWizard
          v-if="showAgentWizard"
          :agent="editingAgent"
          :knowledge-bases="knowledgeBases"
          @close="
            showAgentWizard = false;
            editingAgent = null;
          "
          @created="handleWizardCreated"
          @updated="handleWizardUpdated"
        />
      </CortexSectionGuide>

      <CortexSectionGuide
        v-else-if="activeTab === 'knowledge'"
        :title="t('CORTEX.KNOWLEDGE.LIST_TITLE')"
        :description="t('CORTEX.KNOWLEDGE.LIST_DESCRIPTION')"
        :sections="knowledgeSectionLinks"
        :active-section="activeKnowledgeSection"
        @section-changed="selectKnowledgeSection"
      >
        <template #actions>
          <NextButton sm @click="openCreateKnowledgeBaseDialog">
            <span class="i-lucide-plus size-4" />
            {{ t('CORTEX.KNOWLEDGE.FORM.SUBMIT') }}
          </NextButton>
        </template>

        <div class="grid gap-5">
          <SettingsListGroup class="grid content-start">
            <article
              v-for="knowledgeBase in knowledgeBases"
              :key="knowledgeBase.id"
              class="grid gap-3 px-4 py-3 transition-colors hover:bg-n-alpha-1 md:grid-cols-[minmax(0,1fr)_auto]"
            >
              <button
                type="button"
                class="min-w-0 text-left"
                @click="selectKnowledgeBase(knowledgeBase)"
              >
                <div class="flex items-start gap-3">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <h2 class="font-medium text-n-slate-12">
                        {{ knowledgeBase.name }}
                      </h2>
                      <Label
                        :label="knowledgeBaseStatusLabel(knowledgeBase.status)"
                        :color="knowledgeBaseStatusColor(knowledgeBase.status)"
                        compact
                      >
                        <template #icon>
                          <span class="i-lucide-circle size-2.5 fill-current" />
                        </template>
                      </Label>
                    </div>
                    <p class="mt-1 text-sm text-n-slate-11">
                      {{
                        knowledgeBase.description ||
                        t('CORTEX.KNOWLEDGE.NO_DESCRIPTION')
                      }}
                    </p>
                  </div>
                </div>
              </button>
              <div class="flex items-center gap-2 shrink-0 md:justify-end">
                <NextButton
                  type="button"
                  sm
                  slate
                  ghost
                  icon="i-lucide-pencil"
                  :aria-label="t('CORTEX.KNOWLEDGE.ACTIONS.EDIT')"
                  :title="t('CORTEX.KNOWLEDGE.ACTIONS.EDIT')"
                  @click="editKnowledgeBase(knowledgeBase)"
                />
                <NextButton
                  type="button"
                  sm
                  ruby
                  ghost
                  icon="i-lucide-trash-2"
                  :aria-label="t('CORTEX.KNOWLEDGE.ACTIONS.DELETE')"
                  :title="t('CORTEX.KNOWLEDGE.ACTIONS.DELETE')"
                  :is-loading="
                    isDeletingKnowledgeBase &&
                    selectedKnowledgeBaseId === knowledgeBase.id
                  "
                  @click="
                    selectedKnowledgeBaseId = knowledgeBase.id;
                    deleteSelectedKnowledgeBase();
                  "
                />
              </div>
            </article>
            <div
              v-if="!knowledgeBases.length"
              class="p-8 text-center border border-dashed border-n-weak rounded-lg"
            >
              <span
                class="inline-block i-lucide-book-open size-8 text-n-slate-9"
              />
              <h3 class="mt-3 text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.EMPTY') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.LIST_DESCRIPTION') }}
              </p>
              <NextButton
                blue
                class="mx-auto mt-4"
                @click="openCreateKnowledgeBaseDialog"
              >
                <span class="i-lucide-plus size-4" />
                {{ t('CORTEX.KNOWLEDGE.FORM.SUBMIT') }}
              </NextButton>
            </div>
          </SettingsListGroup>

          <section class="p-4 border border-n-weak rounded-lg">
            <div v-if="selectedKnowledgeBase" class="grid gap-4">
              <div
                v-show="activeKnowledgeSection === 'documents'"
                id="cortex-knowledge-documents"
                class="pt-4 border-t border-n-weak scroll-mt-6"
              >
                <h2 class="text-sm font-medium text-n-slate-12">
                  {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.DESCRIPTION') }}
                </p>
              </div>

              <form
                v-show="activeKnowledgeSection === 'documents'"
                class="grid gap-4"
                @submit.prevent="createDocument"
              >
                <WithLabel
                  name="cortex-document-name"
                  :label="t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.NAME')"
                >
                  <NextInput
                    v-model="documentForm.name"
                    class="w-full"
                    :placeholder="
                      t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.NAME_PLACEHOLDER')
                    "
                    required
                  />
                </WithLabel>
                <WithLabel
                  name="cortex-document-kind"
                  :label="t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.KIND')"
                >
                  <AdaptiveSelect
                    v-model="documentForm.document_kind"
                    :options="documentKindOptions"
                  />
                </WithLabel>
                <WithLabel
                  name="cortex-document-file"
                  :label="t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.FILE')"
                >
                  <label
                    class="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-n-weak hover:border-n-slate-7 cursor-pointer transition-colors text-sm text-n-slate-10 hover:text-n-slate-12"
                  >
                    <span class="i-ph-paperclip text-base" />
                    <span class="truncate">
                      {{
                        selectedDocumentFile?.name ||
                        t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.FILE_PLACEHOLDER')
                      }}
                    </span>
                    <input
                      ref="documentFileInputRef"
                      type="file"
                      class="hidden"
                      accept=".txt,.md,.json,.yaml,.yml,.csv,.xml,.html,.pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.webp,.mp3,.wav,.ogg,.m4a,.mp4,.mov,.webm"
                      required
                      @change="handleDocumentFileChange"
                    />
                  </label>
                </WithLabel>
                <div class="flex flex-wrap justify-end gap-2">
                  <NextButton
                    type="button"
                    sm
                    slate
                    outline
                    icon="i-lucide-eye"
                    :label="t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.PREVIEW')"
                    :is-loading="isPreviewingDocument"
                    :disabled="!selectedDocumentFile"
                    @click="previewDocument"
                  />
                  <NextButton
                    type="submit"
                    sm
                    blue
                    icon="i-lucide-send"
                    :label="t('CORTEX.KNOWLEDGE.DOCUMENTS.FORM.SUBMIT')"
                    :is-loading="isSavingDocument"
                    :disabled="!selectedDocumentFile"
                  />
                </div>
                <section
                  v-if="documentPreview"
                  class="grid gap-3 border border-n-weak rounded-lg p-3"
                >
                  <div class="grid grid-cols-2 gap-2">
                    <div
                      v-for="metric in documentPreviewMetrics"
                      :key="metric.key"
                      class="rounded-lg bg-n-slate-2 p-2"
                    >
                      <p class="text-xs text-n-slate-10">
                        {{ metric.label }}
                      </p>
                      <p class="text-base font-semibold text-n-slate-12">
                        {{ metric.value }}
                      </p>
                    </div>
                  </div>
                  <p class="text-xs text-n-slate-10">
                    {{
                      t('CORTEX.KNOWLEDGE.DOCUMENTS.PREVIEW.EMBEDDINGS', {
                        count:
                          documentPreview.summary?.embeddings_to_update || 0,
                        preserved:
                          documentPreview.summary?.embeddings_preserved || 0,
                      })
                    }}
                  </p>
                  <div class="grid gap-2 max-h-96 overflow-y-auto">
                    <article
                      v-for="section in documentPreview.sections"
                      :key="section.semantic_key"
                      class="rounded-lg border border-n-weak p-2"
                    >
                      <div class="flex items-start justify-between gap-2">
                        <p
                          class="break-all text-xs font-medium text-n-slate-12"
                        >
                          {{ section.semantic_key }}
                        </p>
                        <Label
                          :label="documentPreviewStatusLabel(section.status)"
                          :color="documentPreviewStatusColor(section.status)"
                          compact
                        />
                      </div>
                      <div
                        v-if="section.status !== 'unchanged'"
                        class="mt-2 max-h-40 overflow-auto rounded bg-n-slate-1 p-2 text-xs font-mono"
                      >
                        <p
                          v-for="(line, index) in section.diff"
                          :key="`${section.semantic_key}-${index}`"
                          class="whitespace-pre-wrap"
                          :class="documentDiffLineClass(line.type)"
                        >
                          {{ documentDiffLinePrefix(line.type) }}{{ line.text }}
                        </p>
                      </div>
                    </article>
                  </div>
                </section>
              </form>

              <div
                v-show="activeKnowledgeSection === 'documents'"
                class="grid gap-2"
              >
                <h3 class="text-sm font-medium text-n-slate-12">
                  {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.TITLE') }}
                </h3>
                <div
                  v-if="documentSourceFilter"
                  class="flex items-center justify-between gap-3 rounded-lg bg-n-alpha-2 px-3 py-2"
                >
                  <p class="min-w-0 truncate text-sm text-n-slate-11">
                    {{
                      t('CORTEX_URLS.SOURCES.DOCUMENT_FILTER', {
                        name: documentSourceFilter.name,
                      })
                    }}
                  </p>
                  <NextButton
                    type="button"
                    sm
                    slate
                    ghost
                    icon="i-lucide-x"
                    :aria-label="t('CORTEX_URLS.SOURCES.CLEAR_DOCUMENT_FILTER')"
                    :title="t('CORTEX_URLS.SOURCES.CLEAR_DOCUMENT_FILTER')"
                    @click="clearDocumentSourceFilter"
                  />
                </div>
                <article
                  v-for="document in documents"
                  :key="document.id"
                  class="p-3 border border-n-weak rounded-lg"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                      <h4 class="text-sm font-medium text-n-slate-12">
                        {{ document.name }}
                      </h4>
                      <Label
                        v-if="document.file_type"
                        :data-testid="`cortex-document-file-type-${document.id}`"
                        :label="documentFileTypeBadge(document.file_type).label"
                        :color="documentFileTypeBadge(document.file_type).color"
                        class="mt-1"
                        compact
                      >
                        <template #icon>
                          <Icon
                            :icon="
                              documentFileTypeBadge(document.file_type).icon
                            "
                            class="size-3.5"
                          />
                        </template>
                      </Label>
                    </div>
                    <div class="flex shrink-0 flex-wrap items-center gap-2">
                      <Label
                        :label="documentStatusLabel(document.status)"
                        :color="documentStatusColor(document.status)"
                        compact
                      />
                      <NextButton
                        type="button"
                        sm
                        slate
                        ghost
                        icon="i-lucide-eye"
                        :aria-label="
                          t('CORTEX.KNOWLEDGE.DOCUMENTS.ACTIONS.VIEW')
                        "
                        :title="t('CORTEX.KNOWLEDGE.DOCUMENTS.ACTIONS.VIEW')"
                        @click="viewDocument(document)"
                      />
                      <NextButton
                        type="button"
                        sm
                        ruby
                        ghost
                        icon="i-lucide-trash-2"
                        :aria-label="
                          t('CORTEX.KNOWLEDGE.DOCUMENTS.ACTIONS.DELETE')
                        "
                        :title="t('CORTEX.KNOWLEDGE.DOCUMENTS.ACTIONS.DELETE')"
                        :is-loading="deletingDocumentId === document.id"
                        @click="deleteDocument(document)"
                      />
                    </div>
                  </div>
                </article>
                <p v-if="!documents.length" class="text-sm text-n-slate-11">
                  {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.EMPTY') }}
                </p>
                <PaginationFooter
                  v-if="documentPagination.total"
                  :current-page="documentPagination.page"
                  :total-items="documentPagination.total"
                  :items-per-page="documentPagination.per_page"
                  show-page-size-selector
                  @update:current-page="changeDocumentPage"
                  @update:items-per-page="changeDocumentPerPage"
                />
              </div>

              <div
                v-show="activeKnowledgeSection === 'sources'"
                id="cortex-knowledge-sources"
                class="grid gap-3 pt-4 border-t border-n-weak scroll-mt-6"
              >
                <CortexWebSources
                  :knowledge-base-id="selectedKnowledgeBaseId"
                  :sources="knowledgeSources"
                  @refresh="fetchKnowledgeSources"
                  @configure-help-center="configureHelpCenterSource"
                  @view-documents="viewSourceDocuments"
                />
              </div>

              <form
                v-show="activeKnowledgeSection === 'help_center'"
                id="cortex-knowledge-help-center"
                class="grid gap-4 pt-4 border-t border-n-weak scroll-mt-6"
                @submit.prevent="syncHelpCenter"
              >
                <div>
                  <h3 class="text-sm font-medium text-n-slate-12">
                    {{ t('CORTEX.KNOWLEDGE.HELP_CENTER.TITLE') }}
                  </h3>
                  <p class="mt-1 text-sm text-n-slate-11">
                    {{ t('CORTEX.KNOWLEDGE.HELP_CENTER.DESCRIPTION') }}
                  </p>
                </div>
                <WithLabel
                  name="cortex-help-center-portal"
                  :label="t('CORTEX.KNOWLEDGE.HELP_CENTER.FORM.PORTAL')"
                >
                  <TagMultiSelectComboBox
                    v-model="helpCenterForm.portal_ids"
                    :options="helpCenterPortalOptions"
                    :placeholder="
                      t('CORTEX.KNOWLEDGE.HELP_CENTER.FORM.PORTAL_PLACEHOLDER')
                    "
                    :search-placeholder="
                      t(
                        'CORTEX.KNOWLEDGE.HELP_CENTER.FORM.PORTAL_SEARCH_PLACEHOLDER'
                      )
                    "
                    :empty-state="
                      t('CORTEX.KNOWLEDGE.HELP_CENTER.FORM.PORTAL_EMPTY')
                    "
                  />
                </WithLabel>
                <WithLabel
                  name="cortex-help-center-locale"
                  :label="t('CORTEX.KNOWLEDGE.HELP_CENTER.FORM.LOCALE')"
                >
                  <TagMultiSelectComboBox
                    v-model="helpCenterForm.locales"
                    :options="helpCenterLocaleOptions"
                    :placeholder="
                      t('CORTEX.KNOWLEDGE.HELP_CENTER.FORM.LOCALE_PLACEHOLDER')
                    "
                    :search-placeholder="
                      t(
                        'CORTEX.KNOWLEDGE.HELP_CENTER.FORM.LOCALE_SEARCH_PLACEHOLDER'
                      )
                    "
                    :empty-state="
                      t('CORTEX.KNOWLEDGE.HELP_CENTER.FORM.LOCALE_EMPTY')
                    "
                  />
                </WithLabel>
                <div class="flex justify-end">
                  <NextButton
                    type="submit"
                    sm
                    blue
                    :is-loading="isSyncingHelpCenter"
                  >
                    {{ t('CORTEX.KNOWLEDGE.HELP_CENTER.FORM.SUBMIT') }}
                  </NextButton>
                </div>
              </form>

              <form
                v-show="activeKnowledgeSection === 'structured_import'"
                id="cortex-knowledge-structured-import"
                class="grid gap-4 pt-4 border-t border-n-weak scroll-mt-6"
                @submit.prevent="importStructured"
              >
                <div>
                  <h3 class="text-sm font-medium text-n-slate-12">
                    {{ t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.TITLE') }}
                  </h3>
                  <p class="mt-1 text-sm text-n-slate-11">
                    {{ t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.DESCRIPTION') }}
                  </p>
                </div>
                <WithLabel
                  name="cortex-structured-source"
                  :label="t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.SOURCE')"
                >
                  <NextInput
                    v-model="structuredImportForm.source_name"
                    class="w-full"
                    :placeholder="
                      t(
                        'CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.SOURCE_PLACEHOLDER'
                      )
                    "
                  />
                </WithLabel>
                <WithLabel
                  name="cortex-structured-rows"
                  :label="t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.ROWS')"
                >
                  <TextArea
                    v-model="structuredImportForm.rows"
                    auto-height
                    min-height="8rem"
                    max-height="20rem"
                    :placeholder="
                      t(
                        'CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.ROWS_PLACEHOLDER'
                      )
                    "
                    required
                  />
                </WithLabel>
                <WithLabel
                  name="cortex-structured-schema"
                  :label="t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.SCHEMA')"
                >
                  <TextArea
                    v-model="structuredImportForm.schema"
                    auto-height
                    min-height="6rem"
                    max-height="16rem"
                    :placeholder="
                      t(
                        'CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.SCHEMA_PLACEHOLDER'
                      )
                    "
                  />
                </WithLabel>
                <div class="flex flex-wrap justify-end gap-2">
                  <NextButton
                    type="button"
                    sm
                    slate
                    outline
                    :is-loading="isPreviewingStructuredImport"
                    :disabled="!structuredImportForm.rows"
                    @click="previewStructuredImport"
                  >
                    {{ t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.PREVIEW') }}
                  </NextButton>
                  <NextButton
                    type="submit"
                    sm
                    blue
                    :is-loading="isImportingStructured"
                    :disabled="!structuredImportForm.rows"
                  >
                    {{ t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.FORM.SUBMIT') }}
                  </NextButton>
                </div>
                <div
                  v-if="structuredImportPreview"
                  class="grid grid-cols-2 gap-2 p-3 border border-n-weak rounded-lg"
                >
                  <span class="text-sm text-n-slate-11">
                    {{ t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.PREVIEW.ROWS') }}
                  </span>
                  <span class="text-sm font-medium text-right text-n-slate-12">
                    {{ structuredImportPreview.rows_count }}
                  </span>
                  <span class="text-sm text-n-slate-11">
                    {{ t('CORTEX.KNOWLEDGE.STRUCTURED_IMPORT.PREVIEW.VALID') }}
                  </span>
                  <span class="text-sm font-medium text-right text-n-slate-12">
                    {{ structuredImportPreview.valid_rows_count }}
                  </span>
                </div>
              </form>
            </div>
            <p v-else class="text-sm text-n-slate-11">
              {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.NO_BASE') }}
            </p>
          </section>
        </div>

        <CortexKnowledgeBaseDialog
          v-if="showKnowledgeBaseDialog"
          :knowledge-base="editingKnowledgeBase"
          :portals="portals"
          :categories="helpCenterCategories"
          :is-loading="
            editingKnowledgeBase
              ? isUpdatingKnowledgeBase
              : isSavingKnowledgeBase
          "
          @save="saveKnowledgeBase"
          @close="
            showKnowledgeBaseDialog = false;
            editingKnowledgeBase = null;
          "
        />
      </CortexSectionGuide>

      <section v-else-if="activeTab === 'tools'" class="grid gap-5">
        <header class="grid gap-2">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('CORTEX.TOOLS.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('CORTEX.TOOLS.DESCRIPTION') }}
          </p>
        </header>

        <TabBar
          :tabs="toolTabs"
          :initial-active-tab="activeToolTabIndex"
          @tab-changed="selectToolSection"
        />

        <div
          class="flex min-w-0 items-start gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-n-brand"
          >
            <span class="size-4" :class="activeToolSectionDetails.icon" />
          </span>
          <div class="min-w-0">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ activeToolSectionDetails.label }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ activeToolSectionDetails.description }}
            </p>
          </div>
        </div>

        <section
          v-show="activeToolSection === 'create'"
          id="cortex-tool-create"
          class="grid gap-4 scroll-mt-6"
        >
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.TOOLS.FORM.LIST_TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.TOOLS.FORM.LIST_DESCRIPTION') }}
              </p>
            </div>
            <NextButton
              type="button"
              blue
              icon="i-lucide-plus"
              :label="t('CORTEX.TOOLS.FORM.ADD')"
              @click="openCreateToolDialog"
            />
          </div>
          <SettingsListGroup v-if="externalHttpTools.length">
            <div
              v-for="tool in externalHttpTools"
              :key="tool.id"
              class="flex flex-wrap items-center justify-between gap-4 px-4 py-3"
              @contextmenu="openToolContextMenu($event, tool)"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{ tool.name }}
                  </p>
                  <Label
                    :label="toolMethodLabel(tool.config?.method)"
                    compact
                  />
                </div>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ tool.description || t('CORTEX.TOOLS.NO_DESCRIPTION') }}
                </p>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <Label
                  :label="toolStatusLabel(tool.status)"
                  :color="toolStatusColor(tool.status)"
                  compact
                /><NextButton
                  type="button"
                  sm
                  slate
                  ghost
                  icon="i-lucide-pencil"
                  :aria-label="t('CORTEX.TOOLS.FORM.EDIT')"
                  :title="t('CORTEX.TOOLS.FORM.EDIT')"
                  @click="openEditToolDialog(tool)"
                /><NextButton
                  type="button"
                  sm
                  ruby
                  ghost
                  icon="i-lucide-trash-2"
                  :aria-label="t('CORTEX.TOOLS.FORM.DELETE')"
                  :title="t('CORTEX.TOOLS.FORM.DELETE')"
                  :is-loading="isDeletingToolId === tool.id"
                  @click="deleteTool(tool)"
                />
              </div>
            </div>
          </SettingsListGroup>
          <p v-else class="text-sm text-n-slate-11">
            {{ t('CORTEX.TOOLS.FORM.EMPTY') }}
          </p>
          <CortexToolDialog
            v-if="showToolDialog"
            :tool="editingTool"
            :credentials="externalCredentials"
            :contact-custom-attributes="contactCustomAttributes"
            :is-loading="isSavingTool"
            @save="createTool"
            @test="openToolTestDialog"
            @close="
              showToolDialog = false;
              editingTool = null;
            "
          />
          <CortexToolTestDialog
            v-if="toolTestPayload"
            :variables="toolTestPayload.variables"
            :is-loading="isTestingTool"
            @test="runToolTest"
            @close="toolTestPayload = null"
          />
          <CortexToolTestResultDialog
            v-if="toolTestResult !== null"
            :response="toolTestResult"
            @close="toolTestResult = null"
          />
          <ContextMenu
            v-if="toolContextMenu"
            :x="toolContextMenuPosition.x"
            :y="toolContextMenuPosition.y"
            @close="closeToolContextMenu"
          >
            <div
              role="menu"
              class="grid min-w-44 gap-1 rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg"
            >
              <NextButton
                sm
                slate
                ghost
                icon="i-lucide-pencil"
                :label="t('CORTEX.TOOLS.FORM.EDIT')"
                class="w-full justify-start"
                @click="editToolFromContextMenu"
              />
              <NextButton
                sm
                slate
                ghost
                icon="i-woot-clone"
                :label="t('CORTEX.TOOLS.FORM.DUPLICATE')"
                class="w-full justify-start"
                @click="duplicateTool"
              />
              <div class="my-1 border-t border-n-weak" />
              <NextButton
                sm
                ruby
                ghost
                icon="i-lucide-trash-2"
                :label="t('CORTEX.TOOLS.FORM.DELETE')"
                class="w-full justify-start"
                @click="deleteToolFromContextMenu"
              />
            </div>
          </ContextMenu>
        </section>

        <section
          v-show="activeToolSection === 'native'"
          id="cortex-tool-native"
          class="grid gap-4 p-4 border border-n-weak rounded-lg scroll-mt-6"
        >
          <div>
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('CORTEX.TOOLS.NATIVE.TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('CORTEX.TOOLS.NATIVE.DESCRIPTION') }}
            </p>
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <form
              class="grid gap-3"
              @submit.prevent="createNativeTool('macro')"
            >
              <WithLabel
                name="cortex-native-macro"
                :label="t('CORTEX.TOOLS.NATIVE.FORM.MACRO')"
              >
                <AdaptiveSelect
                  v-model="nativeToolForm.macro_id"
                  :options="macroOptions"
                  :disabled="!macros.length"
                  class="w-full"
                />
              </WithLabel>
              <div class="flex justify-end">
                <NextButton
                  type="submit"
                  sm
                  blue
                  icon="i-lucide-plus"
                  :label="t('CORTEX.TOOLS.NATIVE.FORM.CREATE_FROM_MACRO')"
                  :is-loading="isSavingNativeTool"
                  :disabled="!nativeToolForm.macro_id"
                />
              </div>
            </form>

            <form
              class="grid gap-3"
              @submit.prevent="createNativeTool('automation')"
            >
              <WithLabel
                name="cortex-native-automation"
                :label="t('CORTEX.TOOLS.NATIVE.FORM.AUTOMATION')"
              >
                <AdaptiveSelect
                  v-model="nativeToolForm.automation_rule_id"
                  :options="automationRuleOptions"
                  :disabled="!automationRules.length"
                  class="w-full"
                />
              </WithLabel>
              <div class="flex justify-end">
                <NextButton
                  type="submit"
                  sm
                  blue
                  icon="i-lucide-plus"
                  :label="t('CORTEX.TOOLS.NATIVE.FORM.CREATE_FROM_AUTOMATION')"
                  :is-loading="isSavingNativeTool"
                  :disabled="!nativeToolForm.automation_rule_id"
                />
              </div>
            </form>
          </div>
          <div class="grid gap-3 border-t border-n-weak pt-4">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('CORTEX.TOOLS.NATIVE.LIST_TITLE') }}
            </h3>
            <article
              v-for="tool in paginatedNativeTools"
              :key="tool.id"
              class="p-3 border border-n-weak rounded-lg"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <h4 class="font-medium text-n-slate-12">{{ tool.name }}</h4>
                    <Label :label="toolTypeLabel(tool.tool_type)" compact />
                    <Label
                      :label="toolStatusLabel(tool.status)"
                      :color="toolStatusColor(tool.status)"
                      compact
                    />
                  </div>
                  <p class="mt-1 text-sm text-n-slate-11">
                    {{ tool.description || t('CORTEX.TOOLS.NO_DESCRIPTION') }}
                  </p>
                </div>
                <span
                  class="shrink-0 rounded-md bg-n-alpha-2 px-2 py-1 text-xs"
                >
                  {{ toolRiskLabel(tool.risk_level) }}
                </span>
              </div>
            </article>
            <p v-if="!nativeTools.length" class="text-sm text-n-slate-11">
              {{ t('CORTEX.TOOLS.NATIVE.EMPTY') }}
            </p>
            <PaginationFooter
              v-if="nativeTools.length"
              :current-page="nativeToolPagination.page"
              :total-items="nativeTools.length"
              :items-per-page="nativeToolPagination.per_page"
              show-page-size-selector
              @update:current-page="changeNativeToolPage"
              @update:items-per-page="changeNativeToolPerPage"
            />
          </div>
        </section>

        <section
          v-show="activeToolSection === 'health'"
          id="cortex-tool-health"
          class="grid gap-4 p-4 border border-n-weak rounded-lg scroll-mt-6"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.TOOLS.HEALTH.TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.TOOLS.HEALTH.DESCRIPTION') }}
              </p>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-3">
              <WootDatePicker
                v-model:date-range="toolMonitoringDateRange"
                v-model:range-type="toolMonitoringRangeType"
                @date-range-changed="onToolMonitoringDateRangeChange"
              />
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <NextCheckbox v-model="isToolHealthMonitoringEnabled" />
                {{ t('CORTEX.TOOLS.HEALTH.REAL_TIME') }}
              </label>
              <NextButton
                type="button"
                sm
                outline
                icon="i-lucide-refresh-cw"
                :label="t('CORTEX.TOOLS.HEALTH.REFRESH')"
                :is-loading="isCheckingToolHealth"
                @click="fetchToolHealth"
              />
            </div>
          </div>
          <div v-if="toolHealthchecks.length" class="grid gap-2 sm:grid-cols-3">
            <div
              class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-2"
            >
              <span class="text-sm text-n-slate-11">
                {{ t('CORTEX.TOOLS.HEALTH.SUMMARY.ONLINE') }}
              </span>
              <span class="font-semibold text-n-teal-11">
                {{ toolHealthSummary.online }}
              </span>
            </div>
            <div
              class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-2"
            >
              <span class="text-sm text-n-slate-11">
                {{ t('CORTEX.TOOLS.HEALTH.SUMMARY.OFFLINE') }}
              </span>
              <span class="font-semibold text-n-ruby-11">
                {{ toolHealthSummary.offline }}
              </span>
            </div>
            <div
              class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-2"
            >
              <span class="text-sm text-n-slate-11">
                {{ t('CORTEX.TOOLS.HEALTH.SUMMARY.ATTENTION') }}
              </span>
              <span class="font-semibold text-n-amber-11">
                {{ toolHealthSummary.attention }}
              </span>
            </div>
          </div>
          <div v-if="toolMonitoring" class="grid gap-4">
            <div class="grid gap-2">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.TOOLS.HEALTH.METRICS.TOOLS.TITLE') }}
              </h3>
              <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.TOTAL')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(
                      toolMonitoring.tool_executions.total_executions
                    )
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.COMPLETED')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(
                      toolMonitoring.tool_executions.succeeded_executions
                    )
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.FAILURES')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(
                      toolMonitoring.tool_executions.failed_executions
                    )
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.SUCCESS_RATE')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.SUCCESS_RATE_VALUE', {
                      rate: toolMonitoring.tool_executions.success_rate,
                    })
                  }}</span>
                </div>
              </div>
            </div>
            <div class="grid gap-2">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.TOOLS.HEALTH.METRICS.CONVERSIONS.TITLE') }}
              </h3>
              <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.TOTAL')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(
                      toolMonitoring.media_conversions.total_executions
                    )
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.COMPLETED')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(
                      toolMonitoring.media_conversions.succeeded_executions
                    )
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.FAILURES')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(
                      toolMonitoring.media_conversions.failed_executions
                    )
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.SUCCESS_RATE')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.SUCCESS_RATE_VALUE', {
                      rate: toolMonitoring.media_conversions.success_rate,
                    })
                  }}</span>
                </div>
              </div>
            </div>
            <div class="grid gap-2">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.TOOLS.HEALTH.METRICS.AI_USAGE.TITLE') }}
              </h3>
              <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.AI_USAGE.EVENTS')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(toolMonitoring.ai_usage.events_count)
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.AI_USAGE.INPUT_TOKENS')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(toolMonitoring.ai_usage.input_tokens)
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.AI_USAGE.OUTPUT_TOKENS')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringNumber(
                      toolMonitoring.ai_usage.output_tokens
                    )
                  }}</span>
                </div>
                <div
                  class="flex min-h-20 flex-col justify-between gap-1 rounded-lg bg-n-alpha-2 p-3"
                >
                  <span class="text-sm text-n-slate-11">{{
                    t('CORTEX.TOOLS.HEALTH.METRICS.AI_USAGE.ESTIMATED_COST')
                  }}</span>
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    formatMonitoringCurrency(toolMonitoring.ai_usage.cost_usd)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-n-slate-11">
            {{
              isToolHealthMonitoringEnabled
                ? t('CORTEX.TOOLS.HEALTH.CHECKING')
                : t('CORTEX.TOOLS.HEALTH.PAUSED')
            }}
          </p>
        </section>

        <div v-show="activeToolSection === 'health'" class="grid gap-3">
          <article
            v-for="tool in monitoredTools"
            :key="tool.id"
            class="p-4 border border-n-weak rounded-lg"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <h2 class="font-medium text-n-slate-12">{{ tool.name }}</h2>
                  <Label :label="toolTypeLabel(tool.tool_type)" compact />
                  <Label
                    :label="toolStatusLabel(tool.status)"
                    :color="toolStatusColor(tool.status)"
                    compact
                  />
                  <Label
                    v-if="isSystemTool(tool)"
                    :label="t('CORTEX.TOOLS.BADGES.SYSTEM')"
                    color="brand"
                    compact
                  />
                  <Label
                    v-if="isConversionTool(tool)"
                    :label="t('CORTEX.TOOLS.BADGES.CONVERSION')"
                    color="brand"
                    compact
                  />
                  <Label
                    v-if="isExtractionTool(tool)"
                    :label="t('CORTEX.TOOLS.BADGES.EXTRACTION')"
                    color="brand"
                    compact
                  />
                </div>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ tool.description || t('CORTEX.TOOLS.NO_DESCRIPTION') }}
                </p>
                <div
                  v-if="toolHealthById[tool.id]"
                  class="flex flex-wrap items-center gap-2 mt-2 text-xs text-n-slate-11"
                >
                  <Label
                    :label="
                      toolHealthStatusLabel(toolHealthById[tool.id].status)
                    "
                    :color="
                      toolHealthStatusColor(toolHealthById[tool.id].status)
                    "
                    compact
                  />
                  <span>
                    {{
                      t('CORTEX.TOOLS.HEALTH.LATENCY', {
                        latency: toolHealthById[tool.id].latency_ms,
                      })
                    }}
                  </span>
                  <span>
                    {{
                      t('CORTEX.TOOLS.HEALTH.CHECKED_AT', {
                        time: toolHealthCheckedAt(
                          toolHealthById[tool.id].checked_at
                        ),
                      })
                    }}
                  </span>
                  <span v-if="toolHealthById[tool.id].error">
                    {{ toolHealthErrorLabel(toolHealthById[tool.id].error) }}
                  </span>
                </div>
              </div>
              <span class="text-xs px-2 py-1 rounded-md bg-n-alpha-2 shrink-0">
                {{ toolRiskLabel(tool.risk_level) }}
              </span>
            </div>
          </article>
          <p v-if="!monitoredTools.length" class="text-sm text-n-slate-11">
            {{ t('CORTEX.TOOLS.EMPTY') }}
          </p>
        </div>

        <section
          v-show="activeToolSection === 'executions'"
          id="cortex-tool-executions"
          class="grid gap-3 scroll-mt-6"
        >
          <div>
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('CORTEX.TOOLS.EXECUTIONS.TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('CORTEX.TOOLS.EXECUTIONS.DESCRIPTION') }}
            </p>
          </div>
          <article
            v-for="execution in toolExecutions"
            :key="execution.id"
            class="grid gap-3 p-4 border border-n-weak rounded-lg"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <h3 class="font-medium text-n-slate-12">
                  {{
                    t('CORTEX.TOOLS.EXECUTIONS.ITEM_TITLE', {
                      id: execution.id,
                    })
                  }}
                </h3>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ execution.risk_level }}
                </p>
              </div>
              <span class="text-xs px-2 py-1 rounded-md bg-n-alpha-2">
                {{ execution.status }}
              </span>
            </div>
            <div
              v-if="execution.status === 'pending_approval'"
              class="grid grid-cols-2 gap-2"
            >
              <NextButton
                type="button"
                sm
                teal
                outline
                icon="i-lucide-check"
                :label="t('CORTEX.TOOLS.EXECUTIONS.ACTIONS.APPROVE')"
                :is-loading="isUpdatingToolExecution"
                @click="updateToolExecution(execution, 'approve')"
              />
              <NextButton
                type="button"
                sm
                ruby
                outline
                icon="i-lucide-x"
                :label="t('CORTEX.TOOLS.EXECUTIONS.ACTIONS.REJECT')"
                :is-loading="isUpdatingToolExecution"
                @click="updateToolExecution(execution, 'reject')"
              />
            </div>
          </article>
          <p v-if="!toolExecutions.length" class="text-sm text-n-slate-11">
            {{ t('CORTEX.TOOLS.EXECUTIONS.EMPTY') }}
          </p>
        </section>
      </section>

      <section v-else-if="activeTab === 'playground'" class="grid gap-5">
        <header class="grid w-full min-w-0 gap-2">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('CORTEX.PLAYGROUND.TITLE') }}
          </h2>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('CORTEX.PLAYGROUND.DESCRIPTION') }}
          </p>
        </header>
        <TabBar
          :tabs="playgroundTabs"
          :initial-active-tab="activePlaygroundTabIndex"
          @tab-changed="selectPlaygroundSection"
        />
        <div
          class="flex min-w-0 items-start gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        >
          <span
            class="mt-0.5 size-4 shrink-0 text-n-brand"
            :class="activePlaygroundSectionDetails.icon"
          />
          <div class="min-w-0">
            <h2 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ activePlaygroundSectionDetails.label }}
            </h2>
            <p class="mb-0 mt-1 text-label-small leading-4 text-n-slate-11">
              {{ activePlaygroundSectionDetails.description }}
            </p>
          </div>
        </div>
        <section
          v-show="activePlaygroundSection === 'chat'"
          class="grid gap-5 p-5 border rounded-lg border-n-weak bg-n-surface-2 shadow-sm"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('CORTEX.PLAYGROUND.REAL_RESPONSE.TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.PLAYGROUND.REAL_RESPONSE.DESCRIPTION') }}
              </p>
            </div>
            <Label
              :label="t('CORTEX.PLAYGROUND.REAL_RESPONSE.BADGE')"
              color="teal"
              compact
            >
              <template #icon>
                <span class="i-lucide-circle-dot size-3.5" aria-hidden="true" />
              </template>
            </Label>
          </div>

          <form class="grid gap-4" @submit.prevent="runPlayground">
            <WithLabel
              name="cortex-playground-agent"
              :label="t('CORTEX.PLAYGROUND.FORM.AGENT')"
            >
              <AdaptiveSelect
                v-model="selectedPlaygroundAgentId"
                :options="playgroundAgentOptions"
                class="max-w-md"
                @update:model-value="fetchPlaygroundRuns"
              />
            </WithLabel>
            <div class="grid gap-2">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.PLAYGROUND.FORM.MESSAGE') }}
              </p>
              <TextArea
                v-model="playgroundForm.message"
                class="min-h-28 w-full resize-y"
                :placeholder="t('CORTEX.PLAYGROUND.FORM.MESSAGE_PLACEHOLDER')"
                required
              />
              <div class="flex justify-end">
                <NextButton
                  type="submit"
                  icon="i-lucide-sparkles"
                  :label="t('CORTEX.PLAYGROUND.REAL_RESPONSE.BUTTON')"
                  :is-loading="isRunningPlayground"
                  :disabled="!playgroundForm.message"
                />
              </div>
            </div>
          </form>

          <div class="grid gap-3 pt-5 border-t border-n-weak">
            <div
              class="justify-self-end max-w-[85%] px-3 py-2 text-sm rounded-lg bg-n-brand/10 text-n-brand"
            >
              {{
                latestPlaygroundRun?.input?.message ||
                t('CORTEX.PLAYGROUND.REAL_RESPONSE.MOCK_USER')
              }}
            </div>
            <div class="grid gap-3 max-w-full">
              <div v-if="latestPlaygroundRun" class="relative">
                <TextArea
                  :model-value="
                    playgroundTraining.runId === latestPlaygroundRun.id
                      ? playgroundTraining.answer
                      : latestPlaygroundRun.output?.response_preview
                  "
                  class="min-h-32 w-full resize-y"
                  :aria-label="t('CORTEX.PLAYGROUND.TRAINING.ANSWER')"
                  @update:model-value="
                    updatePlaygroundAnswer(latestPlaygroundRun, $event)
                  "
                />
                <CortexResponseViewerDialog
                  v-if="playgroundRunHasResponse(latestPlaygroundRun)"
                  :content="latestPlaygroundRun.output.response_preview"
                />
              </div>
              <div
                v-else
                class="min-h-32 px-4 py-3 text-sm whitespace-pre-wrap rounded-lg bg-n-alpha-2 text-n-slate-11"
              >
                {{ t('CORTEX.PLAYGROUND.REAL_RESPONSE.MOCK_AGENT') }}
              </div>
              <CortexPlaygroundExecutionFeedback
                v-if="latestPlaygroundRun"
                :execution="latestPlaygroundRun.output?.execution"
              />
              <div
                v-if="playgroundRunHasResponse(latestPlaygroundRun)"
                class="flex flex-wrap items-center justify-between gap-3"
              >
                <AdaptiveSelect
                  v-if="playgroundTraining.runId === latestPlaygroundRun.id"
                  v-model="playgroundTraining.knowledgeBaseId"
                  :options="knowledgeBaseOptions"
                  class="max-w-md"
                  :aria-label="t('CORTEX.PLAYGROUND.TRAINING.KNOWLEDGE_BASE')"
                />
                <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
                  <template
                    v-if="playgroundTraining.runId === latestPlaygroundRun.id"
                  >
                    <NextButton
                      sm
                      slate
                      outline
                      icon="i-lucide-x"
                      :label="t('CORTEX.PLAYGROUND.TRAINING.CANCEL')"
                      @click="playgroundTraining.runId = null"
                    />
                    <NextButton
                      sm
                      icon="i-lucide-save"
                      :is-loading="isSavingPlaygroundTraining"
                      :label="t('CORTEX.PLAYGROUND.TRAINING.SAVE')"
                      @click="savePlaygroundTraining('corrected')"
                    />
                  </template>
                  <template v-else>
                    <NextButton
                      sm
                      outline
                      icon="i-lucide-check"
                      :label="t('CORTEX.PLAYGROUND.TRAINING.CORRECT')"
                      @click="markPlaygroundCorrect(latestPlaygroundRun)"
                    />
                    <NextButton
                      sm
                      outline
                      icon="i-lucide-pencil"
                      :label="t('CORTEX.PLAYGROUND.TRAINING.EDIT')"
                      @click="startPlaygroundTraining(latestPlaygroundRun)"
                    />
                  </template>
                </div>
              </div>
              <p v-else-if="latestPlaygroundRun" class="text-sm text-n-ruby-11">
                {{ t('CORTEX.PLAYGROUND.EXECUTION.TRAINING_DISABLED') }}
              </p>
            </div>
          </div>
        </section>

        <section
          v-show="activePlaygroundSection === 'results'"
          class="grid gap-4 p-5 border rounded-lg border-n-weak"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('CORTEX.PLAYGROUND.RESULTS_TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.PLAYGROUND.RESULTS.DESCRIPTION') }}
              </p>
            </div>
            <Label
              :label="
                t('CORTEX.PLAYGROUND.RESULTS.COUNT', {
                  count: filteredPlaygroundRuns.length,
                })
              "
              color="slate"
              compact
            />
          </div>
          <form
            class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.7fr)_auto]"
            @submit.prevent="applyPlaygroundResultFilters"
          >
            <NextInput
              v-model="playgroundResultFilters.query"
              :placeholder="
                t('CORTEX.PLAYGROUND.RESULTS.FILTERS.SEARCH_PLACEHOLDER')
              "
              :aria-label="t('CORTEX.PLAYGROUND.RESULTS.FILTERS.SEARCH')"
            />
            <AdaptiveSelect
              v-model="playgroundResultFilters.reviewState"
              :options="playgroundResultReviewStateOptions"
              :aria-label="t('CORTEX.PLAYGROUND.RESULTS.FILTERS.STATUS')"
            />
            <NextButton
              type="submit"
              slate
              outline
              icon="i-lucide-list-filter"
              :label="t('CORTEX.PLAYGROUND.RESULTS.FILTERS.APPLY')"
            />
          </form>
          <article
            v-for="run in paginatedPlaygroundRuns"
            :key="run.id"
            class="p-4 border border-n-weak rounded-lg"
          >
            <div class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
              <div>
                <div class="flex items-start justify-between gap-3">
                  <div class="relative min-w-0 flex-1 ltr:pr-8 rtl:pl-8">
                    <h2 class="font-medium text-n-slate-12">
                      {{
                        run.output?.response_preview ||
                        t('CORTEX.PLAYGROUND.NO_OUTPUT')
                      }}
                    </h2>
                    <CortexResponseViewerDialog
                      v-if="playgroundRunHasResponse(run)"
                      :content="run.output.response_preview"
                    />
                  </div>
                  <Label
                    :label="
                      playgroundResultReviewStateOptions.find(
                        option =>
                          option.value === playgroundResultReviewState(run)
                      )?.label || playgroundStatusLabel(run.status)
                    "
                    :color="
                      playgroundResultReviewState(run) === 'correct'
                        ? 'teal'
                        : playgroundResultReviewState(run) === 'incorrect'
                          ? 'ruby'
                          : playgroundResultReviewState(run) ===
                              'needs_correction'
                            ? 'amber'
                            : 'slate'
                    "
                    compact
                  />
                </div>
                <p class="mt-2 text-sm text-n-slate-11">
                  {{ run.input?.message }}
                </p>
                <p
                  v-if="run.output?.response_error"
                  class="mt-2 text-sm text-n-ruby-11"
                >
                  {{ t('CORTEX.PLAYGROUND.RESPONSE_UNAVAILABLE') }}
                </p>
                <CortexPlaygroundExecutionFeedback
                  :execution="run.output?.execution"
                  class="mt-3"
                />
                <div
                  v-if="run.output?.knowledge?.citations?.length"
                  class="flex flex-wrap gap-2 mt-3"
                >
                  <span
                    v-for="citation in run.output.knowledge.citations"
                    :key="citation.entry_id"
                    class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ citation.title }}
                  </span>
                </div>
                <div
                  v-if="run.evaluation?.regression"
                  class="grid gap-1 p-3 mt-3 text-sm rounded-lg bg-n-alpha-2"
                >
                  <p class="font-medium text-n-slate-12">
                    {{ t('CORTEX.PLAYGROUND.REGRESSION.TITLE') }}
                  </p>
                  <p class="text-n-slate-11">
                    {{ t('CORTEX.PLAYGROUND.REGRESSION.EXPECTED_LABEL') }}
                    {{ run.evaluation.regression.expected_answer }}
                  </p>
                  <p class="text-n-slate-11">
                    {{
                      run.evaluation.regression.exact_normalized_text_match
                        ? t('CORTEX.PLAYGROUND.REGRESSION.MATCH')
                        : t('CORTEX.PLAYGROUND.REGRESSION.MISMATCH')
                    }}
                  </p>
                  <p class="text-n-amber-11">
                    {{ t('CORTEX.PLAYGROUND.REGRESSION.REVIEW_REQUIRED') }}
                  </p>
                </div>
              </div>
              <div class="grid gap-2">
                <h3 class="text-sm font-medium text-n-slate-12">
                  {{ t('CORTEX.PLAYGROUND.CHECKS.TITLE') }}
                </h3>
                <div
                  v-for="(value, key) in run.evaluation?.checks"
                  :key="key"
                  class="flex items-center justify-between gap-3 text-sm"
                >
                  <span class="text-n-slate-11">
                    {{ playgroundCheckLabel(key) }}
                  </span>
                  <Label
                    :label="
                      value
                        ? t('CORTEX.PLAYGROUND.CHECKS.YES')
                        : t('CORTEX.PLAYGROUND.CHECKS.NO')
                    "
                    :color="value ? 'teal' : 'amber'"
                    compact
                  />
                </div>
              </div>
            </div>
            <div
              v-if="playgroundRunHasResponse(run)"
              class="flex flex-wrap justify-end gap-2 mt-4"
            >
              <NextButton
                sm
                outline
                icon="i-lucide-check"
                :label="t('CORTEX.PLAYGROUND.TRAINING.CORRECT')"
                @click="markPlaygroundCorrect(run)"
              />
              <NextButton
                sm
                outline
                icon="i-lucide-pencil"
                :label="t('CORTEX.PLAYGROUND.TRAINING.EDIT')"
                @click="startPlaygroundTraining(run)"
              />
              <NextButton
                v-if="run.evaluation?.training?.expected_answer"
                sm
                outline
                icon="i-lucide-rotate-cw"
                :is-loading="isRetestingPlaygroundRunId === run.id"
                :label="t('CORTEX.PLAYGROUND.REGRESSION.RETEST')"
                @click="retestPlaygroundRun(run)"
              />
            </div>
            <div
              v-if="playgroundTraining.runId === run.id"
              class="grid gap-3 pt-4 mt-4 border-t border-n-weak"
            >
              <WithLabel
                :label="t('CORTEX.PLAYGROUND.TRAINING.ANSWER')"
                name="cortex-playground-training-answer"
              >
                <TextArea v-model="playgroundTraining.answer" class="w-full" />
              </WithLabel>
              <WithLabel
                :label="t('CORTEX.PLAYGROUND.TRAINING.KNOWLEDGE_BASE')"
                name="cortex-playground-training-base"
              >
                <AdaptiveSelect
                  v-model="playgroundTraining.knowledgeBaseId"
                  :options="knowledgeBaseOptions"
                  class="w-full"
                />
              </WithLabel>
              <div class="flex justify-end gap-2">
                <NextButton
                  sm
                  outline
                  slate
                  icon="i-lucide-x"
                  :label="t('CORTEX.PLAYGROUND.TRAINING.CANCEL')"
                  @click="playgroundTraining.runId = null"
                />
                <NextButton
                  sm
                  icon="i-lucide-save"
                  :is-loading="isSavingPlaygroundTraining"
                  :label="t('CORTEX.PLAYGROUND.TRAINING.SAVE')"
                  @click="savePlaygroundTraining('corrected')"
                />
              </div>
            </div>
          </article>
          <p
            v-if="!filteredPlaygroundRuns.length"
            class="text-sm text-n-slate-11"
          >
            {{ t('CORTEX.PLAYGROUND.EMPTY') }}
          </p>
          <PaginationFooter
            v-if="filteredPlaygroundRuns.length"
            :current-page="playgroundResultPagination.page"
            :total-items="filteredPlaygroundRuns.length"
            :items-per-page="playgroundResultPagination.per_page"
            show-page-size-selector
            @update:current-page="changePlaygroundResultPage"
            @update:items-per-page="changePlaygroundResultPerPage"
          />
        </section>
      </section>

      <CortexSectionGuide
        v-else-if="activeTab === 'audit'"
        :title="t('CORTEX.AUDIT.TITLE')"
        :description="t('CORTEX.AUDIT.DESCRIPTION')"
        :sections="auditSections"
        active-section="events"
      >
        <article
          v-for="event in auditEvents"
          :key="event.id"
          class="p-4 border border-n-weak rounded-lg"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="font-medium text-n-slate-12">
                {{ event.event_type }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ event.created_at }}
              </p>
            </div>
            <span class="text-xs px-2 py-1 rounded-md bg-n-alpha-2">
              {{ event.severity }}
            </span>
          </div>
        </article>
        <p v-if="!auditEvents.length" class="text-sm text-n-slate-11">
          {{ t('CORTEX.AUDIT.EMPTY') }}
        </p>
      </CortexSectionGuide>

      <CortexSettings v-else-if="activeTab === 'settings'" />
      <CortexUsageCosts v-else-if="activeTab === 'costs'" />
      <CortexProcessingWorkspace
        v-else-if="activeTab === 'processing'"
        data-testid="cortex-processing-workspace"
      />
    </ModuleSettingsLayout>
    <Dialog
      ref="documentViewerDialog"
      :title="
        viewedDocument?.name || t('CORTEX.KNOWLEDGE.DOCUMENTS.VIEWER.TITLE')
      "
      :show-confirm-button="false"
      :cancel-button-label="t('CORTEX.KNOWLEDGE.DOCUMENTS.ACTIONS.CLOSE')"
      width="3xl"
      position="top"
      overflow-y-auto
      @close="clearDocumentViewer"
    >
      <div class="grid min-w-0 gap-4">
        <div class="flex flex-wrap items-center gap-2">
          <Label
            v-if="viewedDocument?.status"
            :label="documentStatusLabel(viewedDocument.status)"
            :color="documentStatusColor(viewedDocument.status)"
            compact
          />
          <Label
            v-if="viewedDocument?.file_type"
            data-testid="cortex-document-viewer-file-type"
            :label="documentFileTypeBadge(viewedDocument.file_type).label"
            :color="documentFileTypeBadge(viewedDocument.file_type).color"
            compact
          >
            <template #icon>
              <Icon
                :icon="documentFileTypeBadge(viewedDocument.file_type).icon"
                class="size-3.5"
              />
            </template>
          </Label>
        </div>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.VIEWER.DESCRIPTION') }}
        </p>
        <div
          v-if="isLoadingDocumentContent"
          class="flex min-h-32 items-center justify-center rounded-lg border border-n-weak text-sm text-n-slate-11"
        >
          {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.VIEWER.LOADING') }}
        </div>
        <div v-else-if="viewedDocumentChunks.length" class="grid gap-3">
          <article
            v-for="chunk in viewedDocumentChunks"
            :key="chunk.id"
            class="grid gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
          >
            <p class="mb-0 text-xs font-medium text-n-slate-11">
              {{
                t('CORTEX.KNOWLEDGE.DOCUMENTS.VIEWER.CHUNK', {
                  position: chunk.position + 1,
                })
              }}
            </p>
            <div
              v-dompurify-html="formattedDocumentChunk(chunk.content)"
              data-testid="cortex-document-markdown"
              class="prose prose-sm min-w-0 max-w-none overflow-x-auto break-words text-n-slate-12 dark:prose-invert prose-headings:font-semibold prose-headings:text-n-slate-12 prose-p:leading-6 prose-p:text-n-slate-12 prose-a:text-n-brand prose-a:underline prose-strong:text-n-slate-12 prose-code:text-n-slate-12 prose-li:text-n-slate-12 [&_blockquote]:border-n-weak [&_blockquote]:text-n-slate-11 [&_code]:rounded [&_code]:bg-n-alpha-black2 [&_code]:px-1 [&_code]:py-0.5 [&_code]:before:content-none [&_code]:after:content-none [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_li>p]:m-0 [&_ol]:my-2 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-n-alpha-black2 [&_pre]:p-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_td]:border [&_td]:border-n-weak [&_td]:px-2 [&_td]:py-1.5 [&_th]:border [&_th]:border-n-weak [&_th]:bg-n-alpha-2 [&_th]:px-2 [&_th]:py-1.5 [&_ul]:my-2"
            />
          </article>
        </div>
        <p v-else class="mb-0 py-8 text-center text-sm text-n-slate-11">
          {{ t('CORTEX.KNOWLEDGE.DOCUMENTS.VIEWER.EMPTY') }}
        </p>
        <PaginationFooter
          v-if="documentContentPagination.total"
          :current-page="documentContentPagination.page"
          :total-items="documentContentPagination.total"
          :items-per-page="documentContentPagination.per_page"
          @update:current-page="fetchDocumentContent"
        />
      </div>
    </Dialog>
    <Dialog
      ref="destructiveActionDialog"
      type="alert"
      :title="t('DIALOG.BUTTONS.CONFIRM')"
      :description="destructiveActionDescription"
      :confirm-button-label="t('DIALOG.BUTTONS.CONFIRM')"
      :is-loading="isRunningDestructiveAction"
      @confirm="confirmDestructiveAction"
      @close="clearDestructiveAction"
    />
  </main>
</template>
